import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../api';
import PortfolioReslotBanner from '../components/PortfolioReslotBanner';
import VelocityAnomalyBanner from '../components/VelocityAnomalyBanner';

const DeliveryCostModal = React.lazy(() => import('../components/DeliveryCostModal'));

interface SlotRequestItem {
  id: number;
  cluster_name: string;
  sku: string;
  product_name: string;
  quantity: number;
}

interface SlotRequestRow {
  id: number;
  request_uid: string;
  request_type: string;
  source_type: string;
  status: string;
  title: string;
  comment?: string;
  source_warehouse: string;
  slot_window_start_date: string | null;
  slot_window_end_date: string | null;
  allow_partial_booking: boolean;
  auto_create_cargo_places?: boolean;
  meta_json?: Record<string, unknown>;
  created_at: string | null;
  earliest_retry_at?: string | null;
  retry_count?: number;
  cancel_reason?: string | null;
  latest_search_event?: SlotRequestEventRow | null;
  items: SlotRequestItem[];
}

interface SupplyItemRow {
  id: number;
  sku: string;
  product_name: string;
  quantity: number;
}

interface SupplyStatusHistoryRow {
  id: number;
  supply_identity?: string;
  supply_state?: string;
  order_state?: string;
  state_updated_at?: string | null;
  last_seen_at?: string | null;
  first_seen_at?: string | null;
  meta_json?: Record<string, unknown> | null;
}

interface SupplyRow {
  id: number;
  draft_uid: string;
  slot_request_id: number | null;
  target_cluster: string;
  source_warehouse: string;
  status: string;
  search_window_start_date?: string | null;
  search_window_end_date?: string | null;
  preferred_slot_date?: string | null;
  timeslot_from_utc: string | null;
  timeslot_to_utc: string | null;
  ozon_order_id?: number | null;
  ozon_supply_id?: number | null;
  meta_json?: Record<string, unknown> | null;
  cargo_places_count: number;
  cargo_operational?: {
    state?: string;
    effective_count?: number;
    local_count?: number;
    local_only_count?: number;
    external_count?: number;
    has_error?: boolean;
    has_pending?: boolean;
    error_message?: string | null;
    created_cargo_places?: number;
    packed_units_total?: number;
  } | null;
  latest_cargo_job?: {
    job_id?: string;
    job_kind?: string;
    status?: string;
    stage?: string;
    message?: string;
    error?: string | null;
    result?: Record<string, unknown> | null;
    started_at?: string | null;
    finished_at?: string | null;
    available_at?: string | null;
  } | null;
  latest_status?: {
    supply_state?: string;
    order_state?: string;
    state_updated_at?: string | null;
    last_seen_at?: string | null;
  } | null;
  status_history?: SupplyStatusHistoryRow[] | null;
  transport_assignment?: {
    id?: number;
    vehicle_id?: number | null;
    driver_id?: number | null;
    assignment_status?: string;
    vehicle?: { brand?: string; license_plate?: string } | null;
    driver?: { full_name?: string; phone?: string } | null;
  } | null;
  items: SupplyItemRow[];
}

interface SupplyQuantityAdjustment {
  status?: string;
  requested_total?: number;
  approved_total?: number;
  missing_total?: number;
  changed_skus?: number;
  needs_followup_slot_search?: boolean;
  cargo_repack_required?: boolean;
  cargo_units_planned?: number;
  synced_at?: string | null;
}

type SupplyDateFilterMode = 'single' | 'range';

interface ClusterSupplyRecommendationBoxPlan {
  box_size: string;
  boxes_used: number;
  units_per_box: number;
  planned_units: number;
}

interface ClusterSupplyRecommendation {
  id: number;
  source_warehouse: string;
  target_cluster: string;
  sku: string;
  product_name?: string | null;
  recommended_qty: number;
  priority_score: number;
  ship_by_at?: string | null;
  eta_at?: string | null;
  expected_revenue_saved?: number | null;
  expected_margin_saved?: number | null;
  boxes_plan_json?: ClusterSupplyRecommendationBoxPlan[] | null;
  meta_json?: Record<string, unknown> | null;
  is_active?: boolean;
}

type RecommendationTaskMode = 'cluster' | 'sku';
type SupplyDateActionMode = 'cargo_fill' | 'registry';

interface RecommendationTaskDetail {
  recommendationId: number;
  clusterName: string;
  sku: string;
  article: string;
  productName: string;
  quantity: number;
  totalBoxes: number;
  boxBySize: Map<string, number>;
  priorityScore: number;
  shipByAt?: string | null;
  etaAt?: string | null;
  expectedRevenueSaved?: number | null;
  expectedMarginSaved?: number | null;
  metaJson?: Record<string, unknown> | null;
}

interface RecommendationTask {
  id: string;
  title: string;
  subtitle: string;
  recommendationIds: number[];
  sourceWarehouse: string;
  totalQuantity: number;
  totalBoxes: number;
  boxBySize: Map<string, number>;
  clusterCount: number;
  skuCount: number;
  priorityScore: number;
  details: RecommendationTaskDetail[];
}

interface TransportVehicleRow {
  id: number;
  brand: string;
  license_plate: string;
  is_active: boolean;
  usage_count?: number;
  last_assigned_at?: string | null;
}

interface TransportDriverRow {
  id: number;
  full_name: string;
  phone: string;
  is_active: boolean;
  usage_count?: number;
  last_assigned_at?: string | null;
}

interface TransportProfileRow {
  profile_key: string;
  vehicle_id: number;
  driver_id: number;
  usage_count?: number;
  last_assigned_at?: string | null;
  vehicle?: TransportVehicleRow | null;
  driver?: TransportDriverRow | null;
}

interface TransportHistoryProfile {
  profileKey: string;
  driverName: string;
  driverPhone: string;
  vehicleBrand: string;
  vehiclePlate: string;
  usageCount: number;
  lastAssignedAt?: string | null;
}

interface SummaryPayload {
  requests_total: number;
  supplies_total: number;
  searching_requests: number;
  slot_booked_supplies: number;
  cargo_packing_required_supplies: number;
  awaiting_shipment_supplies: number;
  in_supply_tracking_supplies: number;
  completed_supplies: number;
}

interface SourceWarehouseResponse {
  options?: string[];
}

interface RecommendationProductOption {
  product_id: number;
  sku: string;
  display_name: string;
  product_alias?: string | null;
}

interface ClusterSupplyCatalogResponse {
  product_catalog?: RecommendationProductOption[];
}

interface SlotRuntimeReadinessPayload {
  ready: boolean;
  checks?: {
    slot_tables?: { ready?: boolean; message?: string };
    credentials?: { ready?: boolean; missing?: string[] };
    ai_runtime?: { ready?: boolean; blocking_reasons?: string[] };
    manual_mode?: { ready?: boolean; message?: string };
  };
}

interface SlotRequestEventRow {
  id: number;
  event_type: string;
  from_status: string;
  to_status: string;
  message: string;
  payload_json?: Record<string, unknown>;
  created_at: string | null;
  created_by_user_id?: number | null;
}

interface BackgroundJobResponse {
  id?: number;
  job_id?: string | null;
  status?: string | null;
  state?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  progress?: {
    stage?: string;
    percent?: number;
    message?: string;
  } | null;
  result?: Record<string, unknown> | null;
  error?: string | null;
  finished_at?: string | null;
}

interface SlotWorkerDiagnosticsPayload {
  total_jobs?: number;
  status_counts?: Record<string, number>;
  retry_scheduled?: number;
  dead_letter?: number;
  averages?: {
    scanned_requests?: number;
    slot_found?: number;
    slot_booked?: number;
    candidates_pending?: number;
    slot_booking_pending?: number;
    slot_booking_failed?: number;
    slot_api_calls?: number;
  };
}

interface OzonSuppliesLiveSnapshotMessage {
  type: 'snapshot';
  summary: SummaryPayload;
  requests: SlotRequestRow[];
  supplies: SupplyRow[];
  generated_at?: string | null;
}

const normalizeSlotRequestRow = (row: SlotRequestRow): SlotRequestRow => ({
  ...row,
  items: Array.isArray(row?.items) ? row.items : [],
});

const normalizeSupplyRow = (row: SupplyRow): SupplyRow => ({
  ...row,
  items: Array.isArray(row?.items) ? row.items : [],
  status_history: Array.isArray(row?.status_history) ? row.status_history : [],
});

type RequestSortKey = 'title' | 'request_type' | 'status' | 'slot' | 'window' | 'source_warehouse' | 'cargo_mode' | 'items';
type SupplySortKey = 'id' | 'target_cluster' | 'status' | 'slot' | 'cargo_places' | 'transport' | 'items';
type SortDirection = 'asc' | 'desc';
type StatusFilterMode = 'neutral' | 'include' | 'exclude';

// Все даты поставок в Ozon — по Москве. Принудительно MSK независимо от
// часового пояса устройства, иначе для пользователей восточнее MSK поставка
// "26 апреля" в LK Ozon рендерилась бы как "27 апреля" (например, UTC+8 даёт
// 26.04 16:00 UTC → 27.04 00:00 локально). Отчёт пользователя 25.04.2026.
const SUPPLY_TIME_ZONE = 'Europe/Moscow';

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: SUPPLY_TIME_ZONE,
  });
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    timeZone: SUPPLY_TIME_ZONE,
  });
};

const summarizeLatestSearchEvent = (event: SlotRequestEventRow | null | undefined): string => {
  if (!event) return 'Поиск еще не запускался';
  const payload = event.payload_json || {};
  if (event.event_type === 'slot_search_enqueued') {
    return 'Поиск поставлен в очередь';
  }
  if (event.event_type === 'slot_search_run') {
    return 'Идет проход поиска слотов';
  }
  if (event.event_type === 'slot_search_result') {
    const found = Number(payload.found_candidates_total || 0);
    const booked = Number(payload.booked_supplies_total || 0);
    return `Последний поиск: найдено ${found}, забронировано ${booked}`;
  }
  return event.message || 'Есть событие поиска';
};

const comparePrimitive = (left: string | number, right: string | number, direction: SortDirection): number => {
  if (typeof left === 'number' || typeof right === 'number') {
    const a = Number(left || 0);
    const b = Number(right || 0);
    return direction === 'asc' ? a - b : b - a;
  }
  const compared = String(left || '').localeCompare(String(right || ''), 'ru', { numeric: true, sensitivity: 'base' });
  return direction === 'asc' ? compared : -compared;
};

const normalizeSearchText = (value: unknown): string =>
  String(value || '')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim();

const formatNumber = (value: number | null | undefined, fractionDigits = 0): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
};

const toDateInputValue = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatCompactDateInput = (value: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || '').trim());
  if (!match) return value || '';
  return `${match[3]}.${match[2]}.${match[1].slice(-2)}`;
};

const parseCompactDateInput = (value: string): string | null => {
  const normalized = String(value || '').trim();
  const match = /^(\d{2})\.(\d{2})\.(\d{2}|\d{4})$/.exec(normalized);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3].length === 2 ? `20${match[3]}` : match[3]);
  const candidate = new Date(Date.UTC(year, month - 1, day));
  if (
    Number.isNaN(candidate.getTime())
    || candidate.getUTCFullYear() !== year
    || candidate.getUTCMonth() !== month - 1
    || candidate.getUTCDate() !== day
  ) {
    return null;
  }
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const addDays = (value: Date, days: number): Date => {
  const next = new Date(value);
  next.setDate(next.getDate() + days);
  return next;
};

const getCurrentWeekRange = () => {
  const today = new Date();
  const start = today;
  const end = addDays(today, 6);
  return {
    start: toDateInputValue(start),
    end: toDateInputValue(end),
  };
};

const getSupplyMeta = (row: SupplyRow): Record<string, unknown> => (
  row.meta_json && typeof row.meta_json === 'object' ? row.meta_json : {}
);

const getSupplySelectedSlotCandidate = (row: SupplyRow): Record<string, unknown> | null => {
  const meta = getSupplyMeta(row);
  const selected = meta.selected_slot_candidate;
  if (selected && typeof selected === 'object' && !Array.isArray(selected)) {
    return selected as Record<string, unknown>;
  }
  const candidates = Array.isArray(meta.slot_candidates) ? meta.slot_candidates : [];
  const first = candidates[0];
  if (first && typeof first === 'object' && !Array.isArray(first)) {
    return first as Record<string, unknown>;
  }
  return null;
};

const getSupplyQuantityAdjustment = (row: SupplyRow): SupplyQuantityAdjustment | null => {
  const meta = getSupplyMeta(row);
  const adjustment = meta.ozon_quantity_adjustment;
  if (!adjustment || typeof adjustment !== 'object' || Array.isArray(adjustment)) return null;
  const normalized = adjustment as Record<string, unknown>;
  const missingTotal = asNumber(normalized.missing_total);
  const requestedTotal = asNumber(normalized.requested_total);
  const approvedTotal = asNumber(normalized.approved_total);
  if (missingTotal === null && requestedTotal === null && approvedTotal === null) return null;
  return {
    status: typeof normalized.status === 'string' ? normalized.status : undefined,
    requested_total: requestedTotal ?? undefined,
    approved_total: approvedTotal ?? undefined,
    missing_total: missingTotal ?? undefined,
    changed_skus: asNumber(normalized.changed_skus) ?? undefined,
    needs_followup_slot_search: Boolean(normalized.needs_followup_slot_search),
    cargo_repack_required: Boolean(normalized.cargo_repack_required),
    cargo_units_planned: asNumber(normalized.cargo_units_planned) ?? undefined,
    synced_at: typeof normalized.synced_at === 'string' ? normalized.synced_at : null,
  };
};

const getSupplyCargoQuantityMismatch = (row: SupplyRow): { approved: number; packed: number } | null => {
  const adjustment = getSupplyQuantityAdjustment(row);
  const approved = Number(adjustment?.approved_total || 0);
  const packed = Number(adjustment?.cargo_units_planned || 0);
  if (approved <= 0 || packed <= 0) return null;
  if (approved === packed) return null;
  return { approved, packed };
};

const isSupplyFollowupRecommendationNeeded = (row: SupplyRow): boolean => {
  const adjustment = getSupplyQuantityAdjustment(row);
  return Boolean(
    adjustment?.needs_followup_slot_search
    && (adjustment?.missing_total || 0) > 0
  );
};

const getSupplySlotDateKey = (row: SupplyRow): string | null => {
  if (row.timeslot_from_utc) {
    return String(row.timeslot_from_utc).slice(0, 10);
  }
  const candidate = getSupplySelectedSlotCandidate(row);
  const slotDate = String(candidate?.slot_date || '').trim();
  if (slotDate) return slotDate;
  const slotFrom = String(candidate?.slot_from_in_timezone || '').trim();
  if (slotFrom.length >= 10) return slotFrom.slice(0, 10);
  return null;
};

const getSupplySlotLabel = (row: SupplyRow): { primary: string; secondary: string | null; source: 'confirmed' | 'candidate' | 'none' } => {
  if (row.timeslot_from_utc) {
    return {
      primary: formatDateTime(row.timeslot_from_utc),
      secondary: row.timeslot_to_utc ? `до ${formatDateTime(row.timeslot_to_utc)}` : null,
      source: 'confirmed',
    };
  }
  const candidate = getSupplySelectedSlotCandidate(row);
  if (!candidate) {
    return { primary: '—', secondary: null, source: 'none' };
  }
  const slotDate = String(candidate.slot_date || '').trim();
  const slotFrom = String(candidate.slot_time_from || '').trim();
  const slotTo = String(candidate.slot_time_to || '').trim();
  const primary = slotDate
    ? `${formatDate(slotDate)}${slotFrom ? ` ${slotFrom}` : ''}${slotTo ? `-${slotTo}` : ''}`
    : formatDateTime(String(candidate.slot_from_in_timezone || ''));
  return {
    primary: primary || '—',
    secondary: primary ? 'Найденный слот' : null,
    source: 'candidate',
  };
};

const isSupplyDateWithinRange = (row: SupplyRow, start: string, end: string) => {
  const slotDate = getSupplySlotDateKey(row);
  if (!slotDate) return false;
  return slotDate >= start && slotDate <= end;
};

const pickNearestSupplyDate = (
  rows: SupplyRow[],
  predicate: (row: SupplyRow) => boolean,
  fallbackDate: string,
): string => {
  const today = toDateInputValue(new Date());
  const dates = Array.from(new Set(
    rows
      .filter(predicate)
      .map(row => getSupplySlotDateKey(row))
      .filter((value): value is string => Boolean(value)),
  )).sort();
  const upcoming = dates.find(value => value >= today);
  return upcoming || dates[0] || fallbackDate;
};

const getDefaultSuppliesDateFilterStart = (
  rows: SupplyRow[],
  fallbackDate: string,
): string => {
  const eligibleStatuses = new Set<string>([
    'awaiting_shipment',
    'in_supply_tracking',
    'completed',
  ]);
  const dates = Array.from(new Set(
    rows
      .filter(row => eligibleStatuses.has(getEffectiveSupplyStatus(row)))
      .map(row => getSupplySlotDateKey(row))
      .filter((value): value is string => Boolean(value)),
  )).sort();
  return dates[0] || fallbackDate;
};

const isRequestBlockingRecommendation = (row: SlotRequestRow, today: string): boolean => {
  const status = String(row.status || '').trim().toLowerCase();
  if (['completed', 'failed', 'cancelled'].includes(status)) return false;
  const referenceDate = String(row.slot_window_end_date || row.slot_window_start_date || '').trim();
  if (referenceDate && referenceDate < today) return false;
  return true;
};

const getSupplyRecommendationReferenceDate = (row: SupplyRow): string => {
  const confirmedSlotDate = row.timeslot_from_utc ? String(row.timeslot_from_utc).slice(0, 10) : '';
  if (confirmedSlotDate) return confirmedSlotDate;
  const searchWindowEnd = String(row.search_window_end_date || '').trim();
  if (searchWindowEnd) return searchWindowEnd;
  const preferredSlotDate = String(row.preferred_slot_date || '').trim();
  if (preferredSlotDate) return preferredSlotDate;
  return getSupplySlotDateKey(row) || '';
};

const getSupplyRawOzonState = (row: SupplyRow): string => {
  const latestStatusState = String(row.latest_status?.supply_state || '').trim().toUpperCase();
  if (latestStatusState) return latestStatusState;
  const metaJson = row.meta_json && typeof row.meta_json === 'object' ? row.meta_json : {};
  return String(metaJson.latest_ozon_supply_state || '').trim().toUpperCase();
};

// Local DB status takes precedence when it is in a terminal state. Otherwise
// Ozon's snapshot may be stale (cancel pending → later confirmed at Ozon but
// our meta.latest_ozon_supply_state was never refreshed), and the UI would
// show a cancelled supply as still active.
const LOCAL_TERMINAL_STATUSES = new Set(['cancelled', 'cancelled_insufficient', 'completed', 'failed']);

const getEffectiveSupplyStatus = (row: SupplyRow): string => {
  const localStatus = String(row.status || '').trim().toLowerCase();
  if (LOCAL_TERMINAL_STATUSES.has(localStatus)) {
    return localStatus;
  }
  const rawSupplyState = getSupplyRawOzonState(row);
  if (rawSupplyState && ozonSupplyStateToStatusMap[rawSupplyState]) {
    return ozonSupplyStateToStatusMap[rawSupplyState];
  }
  return localStatus;
};

const getEffectiveSupplyStatusLabel = (row: SupplyRow): string => {
  const localStatus = String(row.status || '').trim().toLowerCase();
  if (LOCAL_TERMINAL_STATUSES.has(localStatus)) {
    return statusLabelMap[localStatus] || row.status || '—';
  }
  const rawSupplyState = getSupplyRawOzonState(row);
  if (rawSupplyState && ozonSupplyStatusLabelMap[rawSupplyState]) {
    return getOzonSupplyStateLabel(rawSupplyState);
  }
  const status = getEffectiveSupplyStatus(row);
  return statusLabelMap[status] || row.status || '—';
};

const isSupplyExecutionLocked = (row: SupplyRow): boolean => {
  return ['in_supply_tracking', 'completed', 'failed', 'cancelled'].includes(getEffectiveSupplyStatus(row));
};

const isSupplyCancelable = (row: SupplyRow): boolean => {
  return Boolean(row.ozon_order_id) && !isSupplyExecutionLocked(row);
};

const getSupplyStatusHistoryEventTime = (row: SupplyStatusHistoryRow | null | undefined): string | null => {
  if (!row) return null;
  return row.state_updated_at || row.last_seen_at || row.first_seen_at || null;
};

const isSupplyBlockingRecommendation = (row: SupplyRow, today: string): boolean => {
  const status = getEffectiveSupplyStatus(row);
  if (['completed', 'failed', 'cancelled'].includes(status)) return false;
  if (status === 'in_supply_tracking') return true;
  const referenceDate = getSupplyRecommendationReferenceDate(row);
  if (referenceDate && referenceDate < today) return false;
  return true;
};

const hasTransportAssignmentData = (row: SupplyRow) => {
  const assignment = row.transport_assignment;
  const assignmentStatus = String(assignment?.assignment_status || '').trim().toLowerCase();
  const driver = row.transport_assignment?.driver;
  const vehicle = row.transport_assignment?.vehicle;
  return Boolean(
    assignment?.vehicle_id ||
    assignment?.driver_id ||
    assignmentStatus === 'assigned' ||
    assignmentStatus === 'completed' ||
    (driver?.full_name && driver.full_name.trim()) ||
    (driver?.phone && driver.phone.trim()) ||
    (vehicle?.brand && vehicle.brand.trim()) ||
    (vehicle?.license_plate && vehicle.license_plate.trim()),
  );
};

const getLatestCargoJobResult = (row: SupplyRow): Record<string, unknown> => {
  const result = row.latest_cargo_job?.result;
  return result && typeof result === 'object' ? result : {};
};

const getCargoOperational = (row: SupplyRow): Record<string, unknown> => {
  const operational = row.cargo_operational;
  return operational && typeof operational === 'object' ? operational : {};
};

const getCargoJobDiagnosticText = (row: SupplyRow): string => {
  const operational = getCargoOperational(row);
  const result = getLatestCargoJobResult(row);
  const lastErrorMessage = typeof result.last_error_message === 'string' ? result.last_error_message : '';
  return [
    String(operational.error_message || '').trim(),
    String(row.latest_cargo_job?.message || '').trim(),
    String(row.latest_cargo_job?.error || '').trim(),
    lastErrorMessage.trim(),
  ].filter(Boolean).join(' ').toLowerCase();
};

const getDisplayCargoPlacesCount = (row: SupplyRow): number => {
  const operational = getCargoOperational(row);
  const operationalCount = asNumber(operational.effective_count);
  if (operationalCount !== null) {
    return Math.max(0, operationalCount);
  }
  const rawCount = Number(row.cargo_places_count || 0);
  const result = getLatestCargoJobResult(row);
  const createdCargoPlaces = asNumber(result.created_cargo_places);
  const existingBefore = asNumber(result.existing_cargo_places_before);
  const packedUnitsTotal = asNumber(result.packed_units_total);
  const diagnostics = getCargoJobDiagnosticText(row);
  const indicatesNoReadyCargoPlaces = (
    diagnostics.includes('no_local_or_external_cargo_places')
    || diagnostics.includes('supply_item_not_found')
    || (
      createdCargoPlaces === 0
      && (existingBefore === null || existingBefore <= 0)
      && (packedUnitsTotal === null || packedUnitsTotal <= 0)
    )
  );
  if (indicatesNoReadyCargoPlaces) {
    return 0;
  }
  return rawCount;
};

const hasCargoPlacesData = (row: SupplyRow) => {
  return getDisplayCargoPlacesCount(row) > 0;
};

const hasCargoJobError = (row: SupplyRow): boolean => {
  const operational = getCargoOperational(row);
  if (Boolean(operational.has_error)) return true;
  const cargoJobStatus = String(row.latest_cargo_job?.status || '').trim().toLowerCase();
  if (cargoJobStatus === 'failed') return true;
  const result = getLatestCargoJobResult(row);
  const reasons = Array.isArray(result.reasons) ? result.reasons : [];
  return reasons.length > 0;
};

const hasMissingCargoPlaces = (row: SupplyRow): boolean => {
  const operational = getCargoOperational(row);
  if (String(operational.state || '').trim().toLowerCase() === 'pending') return false;
  if (getDisplayCargoPlacesCount(row) > 0) return false;
  if (isSupplyExecutionLocked(row)) return false;
  const status = getEffectiveSupplyStatus(row);
  return ['slot_booked', 'cargo_packing_required', 'awaiting_shipment'].includes(status)
    || Boolean(row.ozon_order_id || row.ozon_supply_id);
};

const isSupplyOverdue = (row: SupplyRow, today: string): boolean => {
  const status = getEffectiveSupplyStatus(row);
  if (['completed', 'failed', 'cancelled'].includes(status)) return false;
  if (status === 'in_supply_tracking') return false;
  const referenceDate = getSupplyRecommendationReferenceDate(row);
  return Boolean(referenceDate && referenceDate < today);
};

const getRecommendationKind = (item: ClusterSupplyRecommendation): string => {
  return String(item.meta_json?.recommendation_kind || 'cluster_supply').trim() || 'cluster_supply';
};

const buildBoxStats = (boxesPlan: ClusterSupplyRecommendationBoxPlan[] | null | undefined) => {
  const bySize = new Map<string, number>();
  let totalBoxes = 0;
  (boxesPlan || []).forEach(row => {
    const boxSize = String(row.box_size || '').trim().toUpperCase();
    const boxesUsed = Number(row.boxes_used || 0);
    if (!boxSize || !Number.isFinite(boxesUsed) || boxesUsed <= 0) return;
    totalBoxes += boxesUsed;
    bySize.set(boxSize, (bySize.get(boxSize) || 0) + boxesUsed);
  });
  return { totalBoxes, bySize };
};

const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(',', '.'));
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const formatMoney = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(value)} ₽`;
};

const formatRatioPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '—';
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value * 100)}%`;
};

const formatBoxBreakdown = (bySize: Map<string, number>): string => {
  const parts = Array.from(bySize.entries())
    .sort(([left], [right]) => left.localeCompare(right, 'ru'))
    .map(([boxSize, count]) => `${boxSize} x${formatNumber(count, 0)}`);
  return parts.length > 0 ? parts.join(', ') : '—';
};

const renderBoxChips = (bySize: Map<string, number>) => {
  const entries = Array.from(bySize.entries()).sort(([left], [right]) => left.localeCompare(right, 'ru'));
  if (entries.length === 0) {
    return <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-500">Нет коробок</span>;
  }
  return entries.map(([boxSize, count]) => (
    <span key={boxSize} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
      {boxSize}: {formatNumber(count, 0)}
    </span>
  ));
};

const priorityMeta = (score: number) => {
  if (score >= 80) {
    return { label: 'Критично', className: 'border-rose-200 bg-rose-50 text-rose-700' };
  }
  if (score >= 60) {
    return { label: 'Высокий', className: 'border-amber-200 bg-amber-50 text-amber-700' };
  }
  if (score >= 40) {
    return { label: 'Средний', className: 'border-blue-200 bg-blue-50 text-blue-700' };
  }
  return { label: 'Низкий', className: 'border-slate-200 bg-slate-50 text-slate-700' };
};

const toMetaArray = (meta: Record<string, unknown> | null | undefined, key: string): unknown[] => {
  if (!meta || typeof meta !== 'object') return [];
  const raw = meta[key];
  return Array.isArray(raw) ? raw : [];
};

const formatPackagingConflictReason = (reason: string | null | undefined): string => {
  const normalized = String(reason || '').trim().toLowerCase();
  if (normalized === 'no_smaller_box_available') {
    return 'нет коробки меньшего размера';
  }
  if (normalized === 'rounding_to_available_boxes') {
    return 'округление до доступных коробок';
  }
  return normalized || 'ограничение по таре';
};

const formatLeadTimeSource = (source: string | null | undefined): string => {
  const normalized = String(source || '').trim().toLowerCase();
  if (normalized === 'procurement_default') return 'базовый норматив закупки';
  if (normalized === 'stock_history_derived') return 'оценка по истории остатков';
  if (normalized === 'status_history_observed') return 'история фактических поставок';
  if (normalized === 'snapshot_history_only') return 'исторические срезы';
  if (normalized === 'ozon_api_average_delivery_time') return 'среднее время доставки OZON';
  if (normalized === 'ozon_api_average_delivery_time+status_history_guardrail') return 'OZON + страховка по истории поставок';
  return normalized || '—';
};

const getRecommendationClusterNeedQty = (detail: RecommendationTaskDetail): number | null => {
  const meta = detail.metaJson && typeof detail.metaJson === 'object' ? detail.metaJson : null;
  const explicitNeed = asNumber(meta?.['cluster_need_qty']);
  if (explicitNeed !== null && explicitNeed > 0) {
    return explicitNeed;
  }
  const allocationPhases = toMetaArray(meta, 'allocation_phases').map(item => String(item || '').trim().toLowerCase());
  if (!allocationPhases.includes('phase_strategic_box_bias')) {
    return explicitNeed;
  }
  const requiredCoverDays = asNumber(meta?.['cluster_required_cover_days']);
  const currentCoverDays = asNumber(meta?.['cluster_days_cover']);
  const velocity = asNumber(meta?.['cluster_velocity_per_day']);
  if (requiredCoverDays === null || currentCoverDays === null || velocity === null || velocity <= 0) {
    return explicitNeed;
  }
  const derivedNeed = Math.max(0, (requiredCoverDays - currentCoverDays) * velocity);
  return derivedNeed > 0 ? derivedNeed : explicitNeed;
};

const buildRecommendationReasonCard = (detail: RecommendationTaskDetail, mode: RecommendationTaskMode) => {
  const meta = detail.metaJson && typeof detail.metaJson === 'object' ? detail.metaJson : {};
  const velocity = asNumber(meta['cluster_velocity_per_day'] ?? meta['debug_current_velocity_per_day']);
  const coverDays = asNumber(meta['cluster_days_cover']);
  const approxStock = velocity !== null && coverDays !== null
    ? velocity * coverDays
    : asNumber(meta['cluster_current_qty'] ?? meta['debug_current_cluster_stock_qty']);
  const clusterNeed = getRecommendationClusterNeedQty(detail);
  const clusterLeadDays = asNumber(meta['cluster_lead_days']);
  const clusterLeadSource = String(meta['cluster_lead_days_source'] || '').trim();
  const clusterDelayBufferDays = asNumber(meta['cluster_delay_buffer_days']);
  const clusterDelayRate = asNumber(meta['cluster_delay_rate']);
  const clusterTotalLeadDays = asNumber(meta['cluster_total_lead_days']);
  const packagingConflictQty = asNumber(meta['packaging_conflict_qty']);
  const packagingMinQty = asNumber(meta['packaging_min_available_qty'] ?? meta['debug_packaging_min_qty']);
  const clusterConflictReasons = toMetaArray(meta, 'packaging_conflict_reasons').map(item => formatPackagingConflictReason(String(item || '').trim())).filter(Boolean);
  const clusterSelectedSizes = toMetaArray(meta, 'packaging_selected_box_sizes').map(item => String(item || '').trim()).filter(Boolean);
  const minBoxSize = String(meta['packaging_min_box_size'] || '').trim();
  const minBoxUnits = asNumber(meta['packaging_min_box_units']);
  const allocationPhases = toMetaArray(meta, 'allocation_phases').map(item => String(item || '').trim());
  const daysUntilNeed = coverDays !== null && clusterTotalLeadDays !== null ? Math.max(0, coverDays - clusterTotalLeadDays) : null;
  const allocationReason =
    allocationPhases.includes('phase_strategic_box_bias')
      ? `Объединение поставки: добор в общую дату${daysUntilNeed !== null ? `, до точки пополнения около ${formatNumber(daysUntilNeed, 1)} дн` : ''}.`
      : allocationPhases.includes('phase_tail_surplus')
        ? 'Пробная или хвостовая отгрузка из свободного остатка.'
        : allocationPhases.some(phase => phase === 'phase_cover_30d' || phase === 'phase_cover_30d_exact')
          ? 'Базовое пополнение под локальное покрытие.'
          : allocationPhases.includes('phase_oos_min')
            ? 'Ускоренное пополнение из-за риска дефицита до следующей ETA.'
            : '';
  const packagingLine = packagingConflictQty !== null && packagingConflictQty > 0
    ? `Тара: минимум ${packagingMinQty !== null ? `${formatNumber(packagingMinQty, 0)} шт` : '—'}${minBoxSize ? ` (${minBoxSize}${minBoxUnits !== null && minBoxUnits > 0 ? ` = ${formatNumber(minBoxUnits, 0)} шт` : ''})` : ''}, перепоставка ${formatNumber(packagingConflictQty, 0)} шт${clusterConflictReasons.length > 0 ? `, причина: ${clusterConflictReasons.join(', ')}` : ''}${clusterSelectedSizes.length > 0 ? `, коробки: ${clusterSelectedSizes.join(', ')}` : ''}.`
    : '';
  const footnote = [
    clusterTotalLeadDays !== null ? `Общее плечо ${formatNumber(clusterTotalLeadDays, 1)} дн` : null,
    clusterDelayBufferDays !== null && clusterDelayBufferDays > 0 ? `буфер ${formatNumber(clusterDelayBufferDays, 1)} дн` : null,
    clusterDelayRate !== null ? `задержка ${formatRatioPercent(clusterDelayRate)}` : null,
    clusterLeadSource ? `источник ${formatLeadTimeSource(clusterLeadSource)}` : null,
  ].filter(Boolean).join(' • ');
  return {
    title: mode === 'cluster' ? detail.article : detail.clusterName,
    badges: [
      `Реком. ${formatNumber(detail.quantity, 0)} шт`,
      clusterNeed !== null && clusterNeed > 0 ? `Потребность ${formatNumber(clusterNeed, 0)} шт` : null,
      Boolean(meta['is_oos_risk']) ? 'Риск дефицита' : null,
      mode === 'cluster' ? `SKU ${detail.sku}` : `Артикул ${detail.article}`,
    ].filter(Boolean) as string[],
    metrics: [
      { label: 'Покрытие', value: coverDays !== null ? `${formatNumber(coverDays, 1)} дн` : '—', tone: coverDays !== null && coverDays < 30 ? 'warning' : 'default' },
      { label: 'Остаток', value: approxStock !== null ? `${formatNumber(approxStock, 0)} шт` : '—', tone: 'default' },
      { label: 'Расход', value: velocity !== null ? `${formatNumber(velocity, 2)} шт/д` : '—', tone: 'default' },
      { label: 'ETA', value: formatDate(detail.etaAt), tone: 'default' },
      { label: 'Потребность', value: clusterNeed !== null ? `${formatNumber(clusterNeed, 0)} шт` : '—', tone: clusterNeed !== null && clusterNeed > 0 ? 'accent' : 'default' },
      { label: 'Плечо', value: clusterLeadDays !== null ? `${formatNumber(clusterLeadDays, 1)} дн` : '—', tone: 'default' },
    ] as Array<{ label: string; value: string; tone: 'default' | 'warning' | 'accent' }>,
    note: [allocationReason, packagingLine].filter(Boolean).join(' '),
    footnote: footnote || undefined,
  };
};

const statusLabelMap: Record<string, string> = {
  draft: 'Черновик',
  searching: 'В поиске',
  slot_found: 'Слот найден',
  slot_booked: 'Забронировано',
  cargo_packing_required: 'Заполнение грузомест',
  awaiting_shipment: 'Ожидает отгрузки',
  in_supply_tracking: 'На точке отгрузки',
  completed: 'Завершено',
  failed: 'Ошибка',
  cancelled: 'Отменено',
  cancelled_insufficient: 'Отменено (недопоставка)',
};

const formatRetryWait = (earliestRetryAt: string | null | undefined): string | null => {
  if (!earliestRetryAt) return null;
  const target = new Date(earliestRetryAt).getTime();
  if (Number.isNaN(target)) return null;
  const diffMs = target - Date.now();
  if (diffMs <= 0) return null;
  const totalMinutes = Math.round(diffMs / 60000);
  if (totalMinutes < 60) return `${totalMinutes} мин`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) return minutes > 0 ? `${hours}ч ${minutes}м` : `${hours}ч`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days}д ${remHours}ч` : `${days}д`;
};

const ozonSupplyStateToStatusMap: Record<string, string> = {
  SLOT_BOOKED_LOCAL: 'slot_booked',
  SLOT_BOOKED_OZON: 'slot_booked',
  DATA_FILLING: 'cargo_packing_required',
  READY_TO_SUPPLY: 'awaiting_shipment',
  IN_TRANSIT: 'in_supply_tracking',
  ACCEPTED_AT_SUPPLY_WAREHOUSE: 'in_supply_tracking',
  REPORTS_CONFIRMATION_AWAITING: 'in_supply_tracking',
  ACCEPTANCE_AT_STORAGE_WAREHOUSE: 'in_supply_tracking',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  REPORT_REJECTED: 'failed',
  REJECTED_AT_SUPPLY_WAREHOUSE: 'failed',
};

const ozonSupplyStatusLabelMap: Record<string, string> = {
  SLOT_BOOKED_LOCAL: 'Слот забронирован локально',
  SLOT_BOOKED_OZON: 'Слот забронирован в OZON',
  DATA_FILLING: 'Заполнение поставки',
  READY_TO_SUPPLY: 'Готова к отгрузке',
  IN_TRANSIT: 'В пути по цепочке OZON',
  ACCEPTED_AT_SUPPLY_WAREHOUSE: 'На точке отгрузки',
  REPORTS_CONFIRMATION_AWAITING: 'Ожидает подтверждения актов',
  ACCEPTANCE_AT_STORAGE_WAREHOUSE: 'Принимается на складе назначения',
  COMPLETED: 'Поставка завершена',
  CANCELLED: 'Поставка отменена',
  REPORT_REJECTED: 'Акт отклонён',
  REJECTED_AT_SUPPLY_WAREHOUSE: 'Отклонена на складе поставки',
};

const getOzonSupplyStateLabel = (rawState: string | null | undefined): string => {
  const normalized = String(rawState || '').trim().toUpperCase();
  if (!normalized) return '—';
  return ozonSupplyStatusLabelMap[normalized] || normalized;
};

const requestTypeLabelMap: Record<string, string> = {
  by_product: 'По товарам',
  by_cluster: 'Кластер',
};

const supplyStatusChipOrder = [
  'draft',
  'searching',
  'slot_found',
  'slot_booked',
  'cargo_packing_required',
  'awaiting_shipment',
  'in_supply_tracking',
  'completed',
  'failed',
  'cancelled',
];

const requestStatusChipOrder = [
  'draft',
  'searching',
  'slot_found',
  'slot_booked',
  'cargo_packing_required',
  'awaiting_shipment',
  'in_supply_tracking',
  'completed',
  'failed',
  'cancelled',
];

const pinnedStatusChip = 'cancelled';
const TERMINAL_SUPPLY_STATUSES = new Set(['completed', 'failed', 'cancelled', 'cancelled_insufficient']);

const defaultStatusFilterState = (): Record<string, StatusFilterMode> => ({
  [pinnedStatusChip]: 'exclude',
});

const cycleStatusFilterMode = (current: StatusFilterMode): StatusFilterMode => {
  if (current === 'neutral') return 'include';
  if (current === 'include') return 'exclude';
  return 'neutral';
};

const liveRequestStatuses = new Set([
  'searching',
  'slot_found',
  'slot_booked',
  'cargo_packing_required',
  'awaiting_shipment',
  'in_supply_tracking',
]);

const liveSupplyStatuses = new Set([
  'draft',
  'slot_found',
  'slot_booked',
  'cargo_packing_required',
  'awaiting_shipment',
  'in_supply_tracking',
]);

const recommendationPlanningKey = (clusterNameRaw: string | null | undefined, skuRaw: string | null | undefined): string => {
  const clusterName = String(clusterNameRaw || '').trim().toLowerCase();
  const sku = String(skuRaw || '').trim();
  if (!clusterName || !sku) return '';
  return `${clusterName}::${sku}`;
};

const cardClass = 'rounded-3xl border border-slate-200 bg-white p-6 shadow-sm';
const transportCacheStorageKey = 'ozon-supplies.transport-cache.v1';

interface EditableRequestItem {
  cluster_name: string;
  sku: string;
  product_name: string;
  quantity: string;
}

const emptyItem = (): EditableRequestItem => ({
  cluster_name: '',
  sku: '',
  product_name: '',
  quantity: '',
});

const downloadBlobFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const IconButton = ({
  title,
  onClick,
  className,
  disabled,
  children,
}: {
  title: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 ${className || ''}`}
  >
    {children}
  </button>
);

const svgClassName = 'h-4 w-4';

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={svgClassName}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={svgClassName}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 10v6" />
    <circle cx="12" cy="7.5" r="0.75" fill="currentColor" stroke="none" />
  </svg>
);

const SortIndicator = ({ active, direction }: { active: boolean; direction: SortDirection }) => (
  <span className={`text-[11px] ${active ? 'text-slate-700' : 'text-slate-300'}`}>{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={svgClassName}>
    <path d="m8 5 11 7-11 7V5Z" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={svgClassName}>
    <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
  </svg>
);

const CancelIcon = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={`${svgClassName} ${className}`.trim()}>
    <circle cx="12" cy="12" r="9" />
    <path d="m15 9-6 6" />
    <path d="m9 9 6 6" />
  </svg>
);

const VehicleIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={`${svgClassName} ${className || ''}`.trim()}>
    <path d="M5 16h14l-1.5-5a2 2 0 0 0-1.9-1.4H8.4A2 2 0 0 0 6.5 11L5 16Z" />
    <path d="M5 16v2a1 1 0 0 0 1 1h1" />
    <path d="M19 16v2a1 1 0 0 1-1 1h-1" />
    <circle cx="8.5" cy="18.5" r="1.5" />
    <circle cx="15.5" cy="18.5" r="1.5" />
  </svg>
);

const CargoIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={`${svgClassName} ${className || ''}`.trim()}>
    <path d="M4.5 8.5 12 4l7.5 4.5v7L12 20l-7.5-4.5v-7Z" />
    <path d="M4.5 8.5 12 13l7.5-4.5" />
    <path d="M12 13v7" />
  </svg>
);

const RegistryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={svgClassName}>
    <path d="M7 4.5h7l3 3V19a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 6 19V6A1.5 1.5 0 0 1 7.5 4.5Z" />
    <path d="M14 4.5V8h3" />
    <path d="M9 11h6" />
    <path d="M9 14.5h6" />
    <path d="M9 18h4" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={svgClassName}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M7.5 3.5v3" />
    <path d="M16.5 3.5v3" />
    <path d="M3.5 9.5h17" />
  </svg>
);

const DatabaseStatusIcon = ({ className }: { className: string }) => (
  <svg viewBox="0 0 20 20" fill="none" className={className} aria-hidden="true">
    <ellipse cx="10" cy="5" rx="5.5" ry="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M4.5 5v4c0 1.4 2.46 2.5 5.5 2.5s5.5-1.1 5.5-2.5V5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M4.5 9v4c0 1.4 2.46 2.5 5.5 2.5s5.5-1.1 5.5-2.5V9" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const KeyStatusIcon = ({ className }: { className: string }) => (
  <svg viewBox="0 0 20 20" fill="none" className={className} aria-hidden="true">
    <circle cx="7" cy="10" r="3.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M10.5 10h5m-1.5 0v-2m-2 2v-1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const AIRuntimeStatusIcon = ({ className }: { className: string }) => (
  <svg viewBox="0 0 20 20" fill="none" className={className} aria-hidden="true">
    <rect x="4.5" y="5" width="11" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 9.5h4M8 12h2.5M10 3.5v1.5M6.5 3.5 7 5M13.5 3.5 13 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const ManualModeStatusIcon = ({ className }: { className: string }) => (
  <svg viewBox="0 0 20 20" fill="none" className={className} aria-hidden="true">
    <path d="M6.5 10.5V6.8a1 1 0 1 1 2 0v2.2m0 1.5V5.8a1 1 0 1 1 2 0v4.7m0 0V7a1 1 0 1 1 2 0v3.5m0 0V8.2a1 1 0 1 1 2 0v4.1c0 2.9-1.9 4.7-4.8 4.7-2.3 0-3.8-1.2-4.7-3.4l-.8-1.8a1 1 0 0 1 1.8-.8l1 2.1Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const OzonSuppliesPage: React.FC = () => {
  const defaultSupplyWeekRange = useMemo(() => getCurrentWeekRange(), []);
  const defaultSupplyToday = useMemo(() => toDateInputValue(new Date()), []);
  const [summary, setSummary] = useState<SummaryPayload | null>(null);
  const [runtimeReadiness, setRuntimeReadiness] = useState<SlotRuntimeReadinessPayload | null>(null);
  const [workerDiagnostics, setWorkerDiagnostics] = useState<SlotWorkerDiagnosticsPayload | null>(null);
  const [requests, setRequests] = useState<SlotRequestRow[]>([]);
  const [supplies, setSupplies] = useState<SupplyRow[]>([]);
  const [liveSocketConnected, setLiveSocketConnected] = useState(false);
  const [suppliesDateFilterOpen, setSuppliesDateFilterOpen] = useState(false);
  const [deliveryCostOpen, setDeliveryCostOpen] = useState(false);
  const [suppliesDateFilterMode, setSuppliesDateFilterMode] = useState<SupplyDateFilterMode>('range');
  const [suppliesDateFilterStart, setSuppliesDateFilterStart] = useState(() => defaultSupplyWeekRange.start);
  const [suppliesDateFilterEnd, setSuppliesDateFilterEnd] = useState(() => defaultSupplyWeekRange.end);
  const [suppliesDateFilterDraftMode, setSuppliesDateFilterDraftMode] = useState<SupplyDateFilterMode>('range');
  const [suppliesDateFilterDraftStart, setSuppliesDateFilterDraftStart] = useState(() => defaultSupplyWeekRange.start);
  const [suppliesDateFilterDraftEnd, setSuppliesDateFilterDraftEnd] = useState(() => defaultSupplyWeekRange.end);
  const [suppliesDateFilterDefaultApplied, setSuppliesDateFilterDefaultApplied] = useState(false);
  const [activeSupplyStatusFilters, setActiveSupplyStatusFilters] = useState<Record<string, StatusFilterMode>>(() => defaultStatusFilterState());
  const [supplyActiveOnly, setSupplyActiveOnly] = useState(true);
  const [recommendations, setRecommendations] = useState<ClusterSupplyRecommendation[]>([]);
  const [recommendationProductCatalog, setRecommendationProductCatalog] = useState<RecommendationProductOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formBusy, setFormBusy] = useState(false);
  const [formNotice, setFormNotice] = useState('');
  const [requestEditorOpen, setRequestEditorOpen] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState<number | null>(null);
  const [requestType, setRequestType] = useState<'by_product' | 'by_cluster'>('by_product');
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [sourceWarehouse, setSourceWarehouse] = useState('');
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [allowPartialBooking, setAllowPartialBooking] = useState(true);
  const [autoCreateCargoPlaces, setAutoCreateCargoPlaces] = useState(true);
  const [items, setItems] = useState<EditableRequestItem[]>([emptyItem()]);
  const [bulkDate, setBulkDate] = useState('');
  const [bulkDateField, setBulkDateField] = useState<'slot_window_start_date' | 'slot_window_end_date' | 'created_date'>('slot_window_start_date');
  const [bulkAction, setBulkAction] = useState<'start_search' | 'stop_search' | 'cancel'>('start_search');
  const [selectedAuditRequestId, setSelectedAuditRequestId] = useState<number | null>(null);
  const [selectedAuditSupplyId, setSelectedAuditSupplyId] = useState<number | null>(null);
  const [selectedAuditSupplyDetail, setSelectedAuditSupplyDetail] = useState<SupplyRow | null>(null);
  const [auditModalTitle, setAuditModalTitle] = useState('Информация');
  const [auditEvents, setAuditEvents] = useState<SlotRequestEventRow[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [cargoFillDate, setCargoFillDate] = useState(() => defaultSupplyToday);
  const [supplyDateActionMode, setSupplyDateActionMode] = useState<SupplyDateActionMode | null>(null);
  const [supplyDateActionDraftMode, setSupplyDateActionDraftMode] = useState<SupplyDateFilterMode>('single');
  const [supplyDateActionDraft, setSupplyDateActionDraft] = useState(() => defaultSupplyToday);
  const [supplyDateActionDraftEnd, setSupplyDateActionDraftEnd] = useState(() => defaultSupplyToday);
  const [profiles, setProfiles] = useState<TransportProfileRow[]>([]);
  const [transportVehicles, setTransportVehicles] = useState<TransportVehicleRow[]>([]);
  const [transportDrivers, setTransportDrivers] = useState<TransportDriverRow[]>([]);
  const [transportCacheEditorOpen, setTransportCacheEditorOpen] = useState(false);
  const [transportCacheInitialized, setTransportCacheInitialized] = useState(false);
  const [transportAssigningSupplyIds, setTransportAssigningSupplyIds] = useState<number[]>([]);
  const [supplyCancellingIds, setSupplyCancellingIds] = useState<number[]>([]);
  const [transportEditorDriverName, setTransportEditorDriverName] = useState('');
  const [transportEditorDriverPhone, setTransportEditorDriverPhone] = useState('');
  const [transportEditorVehicleBrand, setTransportEditorVehicleBrand] = useState('');
  const [transportEditorVehiclePlate, setTransportEditorVehiclePlate] = useState('');
  const [recommendationsModalOpen, setRecommendationsModalOpen] = useState(false);
  const [recommendationTaskMode, setRecommendationTaskMode] = useState<RecommendationTaskMode>('cluster');
  const [expandedRecommendationTaskIds, setExpandedRecommendationTaskIds] = useState<string[]>([]);
  const [expandedRecommendationReasonIds, setExpandedRecommendationReasonIds] = useState<number[]>([]);
  const [disabledRecommendationTaskIds, setDisabledRecommendationTaskIds] = useState<string[]>([]);
  const [excludedRecommendationIds, setExcludedRecommendationIds] = useState<number[]>([]);
  const [recommendationQtyOverrides, setRecommendationQtyOverrides] = useState<Map<number, number>>(new Map());
  const [recommendationBoxOverrides, setRecommendationBoxOverrides] = useState<Map<number, ClusterSupplyRecommendationBoxPlan[]>>(new Map());
  const [recommendationSourceWarehouseOverrides, setRecommendationSourceWarehouseOverrides] = useState<Record<string, string>>({});
  const [recommendationWarehouseOptions, setRecommendationWarehouseOptions] = useState<string[]>([]);
  const [bulkRecommendationWarehouse, setBulkRecommendationWarehouse] = useState('');
  const [recommendationWarehouseNotice, setRecommendationWarehouseNotice] = useState('');
  const [recommendationWindowStart, setRecommendationWindowStart] = useState(() => toDateInputValue(addDays(new Date(), 1)));
  const [recommendationWindowEnd, setRecommendationWindowEnd] = useState(() => toDateInputValue(addDays(new Date(), 15)));
  const [recommendationWindowStartDisplay, setRecommendationWindowStartDisplay] = useState(() => formatCompactDateInput(toDateInputValue(addDays(new Date(), 1))));
  const [recommendationWindowEndDisplay, setRecommendationWindowEndDisplay] = useState(() => formatCompactDateInput(toDateInputValue(addDays(new Date(), 15))));
  const [recommendationTrackingEnabled, setRecommendationTrackingEnabled] = useState(true);
  const [recommendationSubmitBusy, setRecommendationSubmitBusy] = useState(false);
  const [recommendationPlanningBusy, setRecommendationPlanningBusy] = useState(false);
  const [clusterSupplyRecomputeJob, setClusterSupplyRecomputeJob] = useState<BackgroundJobResponse | null>(null);
  const [manualRecomputeBusy, setManualRecomputeBusy] = useState<boolean>(false);
  const [requestSortKey, setRequestSortKey] = useState<RequestSortKey>('title');
  const [requestSortDirection, setRequestSortDirection] = useState<SortDirection>('asc');
  const [supplySortKey, setSupplySortKey] = useState<SupplySortKey>('slot');
  const [supplySortDirection, setSupplySortDirection] = useState<SortDirection>('asc');
  const [activeRequestStatusFilters, setActiveRequestStatusFilters] = useState<Record<string, StatusFilterMode>>(() => defaultStatusFilterState());
  const [requestSearch, setRequestSearch] = useState('');
  const [supplySearch, setSupplySearch] = useState('');

  const transportHistoryProfiles = useMemo<TransportHistoryProfile[]>(() => {
    const uniqueKeys = new Set<string>();
    const items: TransportHistoryProfile[] = [];
    profiles.forEach(profile => {
      const driverName = String(profile.driver?.full_name || '').trim();
      const driverPhone = String(profile.driver?.phone || '').trim();
      const vehicleBrand = String(profile.vehicle?.brand || '').trim();
      const vehiclePlate = String(profile.vehicle?.license_plate || '').trim();
      const key = [driverName.toLowerCase(), driverPhone, vehicleBrand.toLowerCase(), vehiclePlate.toLowerCase()].join('#');
      if (!key.replace(/#/g, '')) return;
      if (uniqueKeys.has(key)) return;
      uniqueKeys.add(key);
      items.push({
        profileKey: String(profile.profile_key || key),
        driverName,
        driverPhone,
        vehicleBrand,
        vehiclePlate,
        usageCount: Number(profile.usage_count || 0),
        lastAssignedAt: profile.last_assigned_at || null,
      });
    });
    return items;
  }, [profiles]);

  const topTransportProfiles = useMemo(
    () => transportHistoryProfiles.slice(0, 12),
    [transportHistoryProfiles],
  );

  const latestAssignedTransportProfile = useMemo<TransportHistoryProfile | null>(() => (
    topTransportProfiles[0] || null
  ), [topTransportProfiles]);

  const transportDriverNameOptions = useMemo(() => Array.from(new Set([
    ...transportHistoryProfiles.map(profile => profile.driverName).filter(Boolean),
    ...transportDrivers.map(item => String(item.full_name || '').trim()).filter(Boolean),
  ])), [transportDrivers, transportHistoryProfiles]);

  const transportDriverPhoneOptions = useMemo(() => Array.from(new Set([
    ...transportHistoryProfiles.map(profile => profile.driverPhone).filter(Boolean),
    ...transportDrivers.map(item => String(item.phone || '').trim()).filter(Boolean),
  ])), [transportDrivers, transportHistoryProfiles]);

  const transportVehiclePlateOptions = useMemo(() => Array.from(new Set([
    ...transportHistoryProfiles.map(profile => profile.vehiclePlate).filter(Boolean),
    ...transportVehicles.map(item => String(item.license_plate || '').trim()).filter(Boolean),
  ])), [transportHistoryProfiles, transportVehicles]);

  const transportVehicleBrandOptions = useMemo(() => Array.from(new Set([
    ...transportHistoryProfiles.map(profile => profile.vehicleBrand).filter(Boolean),
    ...transportVehicles.map(item => String(item.brand || '').trim()).filter(Boolean),
  ])), [transportHistoryProfiles, transportVehicles]);

  const transportCacheReady = useMemo(() => (
    Boolean(
      transportEditorDriverName.trim() &&
      transportEditorDriverPhone.trim() &&
      transportEditorVehicleBrand.trim() &&
      transportEditorVehiclePlate.trim()
    )
  ), [
    transportEditorDriverName,
    transportEditorDriverPhone,
    transportEditorVehicleBrand,
    transportEditorVehiclePlate,
  ]);

  const suppliesByDateFilter = useMemo(() => {
    const start = suppliesDateFilterStart;
    const end = suppliesDateFilterMode === 'single' ? suppliesDateFilterStart : suppliesDateFilterEnd;
    return supplies.filter(row => isSupplyDateWithinRange(row, start, end));
  }, [supplies, suppliesDateFilterEnd, suppliesDateFilterMode, suppliesDateFilterStart]);

  const nearestRegistryDate = useMemo(() => (
    pickNearestSupplyDate(
      supplies,
      row => !['cancelled', 'failed'].includes(getEffectiveSupplyStatus(row)),
      defaultSupplyToday,
    )
  ), [defaultSupplyToday, supplies]);

  const defaultSuppliesDateFilterStart = useMemo(
    () => getDefaultSuppliesDateFilterStart(supplies, defaultSupplyWeekRange.start),
    [defaultSupplyWeekRange.start, supplies],
  );

  const activeShipmentPeriodStartDate = useMemo(() => {
    return supplies
      .filter(row => ['awaiting_shipment', 'in_supply_tracking'].includes(getEffectiveSupplyStatus(row)))
      .map(row => getSupplySlotDateKey(row))
      .filter((value): value is string => Boolean(value && value < defaultSupplyToday))
      .sort()[0] || '';
  }, [defaultSupplyToday, supplies]);

  const nearestCargoFillDate = useMemo(() => (
    pickNearestSupplyDate(
      supplies,
      row => {
        const status = getEffectiveSupplyStatus(row);
        if (getDisplayCargoPlacesCount(row) > 0) return false;
        if (['cancelled', 'failed', 'completed'].includes(status)) return false;
        return ['slot_booked', 'cargo_packing_required', 'awaiting_shipment', 'in_supply_tracking'].includes(status)
          || Boolean(row.ozon_order_id || row.ozon_supply_id);
      },
      defaultSupplyToday,
    )
  ), [defaultSupplyToday, supplies]);

  useEffect(() => {
    if (loading || suppliesDateFilterDefaultApplied) return;
    const nextStart = defaultSuppliesDateFilterStart || defaultSupplyWeekRange.start;
    const nextEnd = nextStart > defaultSupplyWeekRange.end ? nextStart : defaultSupplyWeekRange.end;
    setSuppliesDateFilterMode('range');
    setSuppliesDateFilterStart(nextStart);
    setSuppliesDateFilterEnd(nextEnd);
    setSuppliesDateFilterDraftMode('range');
    setSuppliesDateFilterDraftStart(nextStart);
    setSuppliesDateFilterDraftEnd(nextEnd);
    setSuppliesDateFilterDefaultApplied(true);
  }, [
    defaultSuppliesDateFilterStart,
    defaultSupplyWeekRange.end,
    defaultSupplyWeekRange.start,
    loading,
    suppliesDateFilterDefaultApplied,
  ]);

  const supplyStatusChips = useMemo(() => {
    const counts = new Map<string, number>();
    suppliesByDateFilter.forEach(row => {
      const status = getEffectiveSupplyStatus(row);
      if (!status) return;
      counts.set(status, (counts.get(status) || 0) + 1);
    });
    return supplyStatusChipOrder
      .filter(status => (counts.get(status) || 0) > 0)
      .map(status => ({
        status,
        label: statusLabelMap[status] || status,
        count: counts.get(status) || 0,
        mode: activeSupplyStatusFilters[status] || 'neutral',
      }));
  }, [activeSupplyStatusFilters, suppliesByDateFilter]);

  const filteredSupplies = useMemo(() => {
    const includedStatuses = new Set(
      Object.entries(activeSupplyStatusFilters)
        .filter(([, mode]) => mode === 'include')
        .map(([status]) => String(status || '').trim().toLowerCase())
        .filter(Boolean),
    );
    const excludedStatuses = new Set(
      Object.entries(activeSupplyStatusFilters)
        .filter(([, mode]) => mode === 'exclude')
        .map(([status]) => String(status || '').trim().toLowerCase())
        .filter(Boolean),
    );
    const normalizedSearch = normalizeSearchText(supplySearch);
    return suppliesByDateFilter.filter(row => {
      const normalizedStatus = getEffectiveSupplyStatus(row);
      if (supplyActiveOnly && TERMINAL_SUPPLY_STATUSES.has(normalizedStatus)) return false;
      const statusMatches = (includedStatuses.size === 0 || includedStatuses.has(normalizedStatus)) && !excludedStatuses.has(normalizedStatus);
      if (!statusMatches) return false;
      if (!normalizedSearch) return true;
      const transport = row.transport_assignment || {};
      const vehicle = transport.vehicle || {};
      const driver = transport.driver || {};
      const slotLabel = getSupplySlotLabel(row);
      const haystack = normalizeSearchText([
        row.id,
        row.draft_uid,
        row.target_cluster,
        row.source_warehouse,
        normalizedStatus,
        getEffectiveSupplyStatusLabel(row),
        row.status,
        slotLabel.primary,
        slotLabel.secondary,
        row.ozon_order_id,
        row.ozon_supply_id,
        row.latest_status?.supply_state,
        row.latest_status?.order_state,
        vehicle.brand,
        vehicle.license_plate,
        driver.full_name,
        driver.phone,
        ...row.items.flatMap(item => [item.sku, item.product_name, item.quantity]),
      ].join(' '));
      return haystack.includes(normalizedSearch);
    });
  }, [activeSupplyStatusFilters, suppliesByDateFilter, supplyActiveOnly, supplySearch]);

  const requestStatusChips = useMemo(() => {
    const counts = new Map<string, number>();
    requests.forEach(row => {
      const status = String(row.status || '').trim().toLowerCase();
      if (!status) return;
      counts.set(status, (counts.get(status) || 0) + 1);
    });
    return requestStatusChipOrder
      .filter(status => (counts.get(status) || 0) > 0)
      .map(status => ({
        status,
        label: statusLabelMap[status] || status,
        count: counts.get(status) || 0,
        mode: activeRequestStatusFilters[status] || 'neutral',
      }));
  }, [activeRequestStatusFilters, requests]);

  const filteredRequests = useMemo(() => {
    const includedStatuses = new Set(
      Object.entries(activeRequestStatusFilters)
        .filter(([, mode]) => mode === 'include')
        .map(([status]) => String(status || '').trim().toLowerCase())
        .filter(Boolean),
    );
    const excludedStatuses = new Set(
      Object.entries(activeRequestStatusFilters)
        .filter(([, mode]) => mode === 'exclude')
        .map(([status]) => String(status || '').trim().toLowerCase())
        .filter(Boolean),
    );
    const normalizedSearch = normalizeSearchText(requestSearch);
    return requests.filter(row => {
      const normalizedStatus = String(row.status || '').trim().toLowerCase();
      const statusMatches = (includedStatuses.size === 0 || includedStatuses.has(normalizedStatus)) && !excludedStatuses.has(normalizedStatus);
      if (!statusMatches) return false;
      if (!normalizedSearch) return true;
      const relatedSupplies = supplies.filter(item => Number(item.slot_request_id || 0) === row.id);
      const haystack = normalizeSearchText([
        row.id,
        row.request_uid,
        row.request_type,
        row.source_type,
        row.status,
        row.title,
        row.comment,
        row.source_warehouse,
        row.slot_window_start_date,
        row.slot_window_end_date,
        row.latest_search_event?.message,
        ...relatedSupplies.flatMap(item => {
          const slotLabel = getSupplySlotLabel(item);
          return [
            item.id,
            item.target_cluster,
            getEffectiveSupplyStatus(item),
            getEffectiveSupplyStatusLabel(item),
            item.status,
            item.ozon_order_id,
            item.ozon_supply_id,
            slotLabel.primary,
            slotLabel.secondary,
          ];
        }),
        ...row.items.flatMap(item => [item.sku, item.product_name, item.cluster_name, item.quantity]),
      ].join(' '));
      return haystack.includes(normalizedSearch);
    });
  }, [activeRequestStatusFilters, requestSearch, requests, supplies]);

  const requestSupplySummaryByRequestId = useMemo(() => {
    const map = new Map<number, {
      slotLabel: { primary: string; secondary: string | null; source: 'confirmed' | 'candidate' | 'none' };
      ozonOrderId: number | null;
      ozonSupplyId: number | null;
      suppliesCount: number;
      quantityAdjustment: SupplyQuantityAdjustment | null;
    }>();
    requests.forEach(request => {
      const relatedSupplies = supplies
        .filter(item => Number(item.slot_request_id || 0) === request.id)
        .sort((left, right) => {
          const leftConfirmed = left.ozon_supply_id || left.ozon_order_id || left.timeslot_from_utc ? 1 : 0;
          const rightConfirmed = right.ozon_supply_id || right.ozon_order_id || right.timeslot_from_utc ? 1 : 0;
          if (rightConfirmed !== leftConfirmed) return rightConfirmed - leftConfirmed;
          return left.id - right.id;
        });
      if (relatedSupplies.length === 0) return;
      const selected = relatedSupplies[0];
      map.set(request.id, {
        slotLabel: getSupplySlotLabel(selected),
        ozonOrderId: selected.ozon_order_id ? Number(selected.ozon_order_id) : null,
        ozonSupplyId: selected.ozon_supply_id ? Number(selected.ozon_supply_id) : null,
        suppliesCount: relatedSupplies.length,
        quantityAdjustment: getSupplyQuantityAdjustment(selected),
      });
    });
    return map;
  }, [requests, supplies]);

  const selectedAuditRequestRow = useMemo(() => {
    if (selectedAuditRequestId === null) return null;
    return requests.find(row => row.id === selectedAuditRequestId) || null;
  }, [requests, selectedAuditRequestId]);

  const selectedAuditRequestSupplySummary = useMemo(() => {
    if (selectedAuditRequestId === null) return null;
    return requestSupplySummaryByRequestId.get(selectedAuditRequestId) || null;
  }, [requestSupplySummaryByRequestId, selectedAuditRequestId]);

  const selectedAuditSupplyRow = useMemo(() => {
    if (selectedAuditSupplyId === null) return null;
    if (selectedAuditSupplyDetail && selectedAuditSupplyDetail.id === selectedAuditSupplyId) {
      return selectedAuditSupplyDetail;
    }
    return supplies.find(row => row.id === selectedAuditSupplyId) || null;
  }, [selectedAuditSupplyDetail, selectedAuditSupplyId, supplies]);

  const sortedRequests = useMemo(() => {
    const rows = [...filteredRequests];
    rows.sort((left, right) => {
      const leftSummary = requestSupplySummaryByRequestId.get(left.id);
      const rightSummary = requestSupplySummaryByRequestId.get(right.id);
      switch (requestSortKey) {
        case 'request_type':
          return comparePrimitive(left.request_type || '', right.request_type || '', requestSortDirection);
        case 'status':
          return comparePrimitive(statusLabelMap[left.status] || left.status || '', statusLabelMap[right.status] || right.status || '', requestSortDirection);
        case 'slot':
          return comparePrimitive(leftSummary?.slotLabel.primary || '', rightSummary?.slotLabel.primary || '', requestSortDirection);
        case 'window':
          return comparePrimitive(left.slot_window_start_date || '', right.slot_window_start_date || '', requestSortDirection);
        case 'source_warehouse':
          return comparePrimitive(left.source_warehouse || '', right.source_warehouse || '', requestSortDirection);
        case 'cargo_mode':
          return comparePrimitive(left.auto_create_cargo_places === false ? 0 : 1, right.auto_create_cargo_places === false ? 0 : 1, requestSortDirection);
        case 'items':
          return comparePrimitive(left.items.length, right.items.length, requestSortDirection);
        case 'title':
        default:
          return comparePrimitive(left.title || `Заявка #${left.id}`, right.title || `Заявка #${right.id}`, requestSortDirection);
      }
    });
    return rows;
  }, [filteredRequests, requestSortDirection, requestSortKey, requestSupplySummaryByRequestId]);

  const sortedSupplies = useMemo(() => {
    const rows = [...filteredSupplies];
    rows.sort((left, right) => {
      switch (supplySortKey) {
        case 'id':
          return comparePrimitive(left.id, right.id, supplySortDirection);
        case 'target_cluster':
          return comparePrimitive(left.target_cluster || '', right.target_cluster || '', supplySortDirection);
        case 'status':
          return comparePrimitive(
            getEffectiveSupplyStatusLabel(left),
            getEffectiveSupplyStatusLabel(right),
            supplySortDirection,
          );
        case 'cargo_places':
          return comparePrimitive(getDisplayCargoPlacesCount(left), getDisplayCargoPlacesCount(right), supplySortDirection);
        case 'transport':
          return comparePrimitive(
            left.transport_assignment?.driver?.full_name || left.transport_assignment?.vehicle?.license_plate || '',
            right.transport_assignment?.driver?.full_name || right.transport_assignment?.vehicle?.license_plate || '',
            supplySortDirection,
          );
        case 'items':
          return comparePrimitive(left.items.length, right.items.length, supplySortDirection);
        case 'slot':
        default:
          return comparePrimitive(getSupplySlotLabel(left).primary, getSupplySlotLabel(right).primary, supplySortDirection);
      }
    });
    return rows;
  }, [filteredSupplies, supplySortDirection, supplySortKey]);

  const resolvedBulkTransportDate = useMemo(() => {
    if (suppliesDateFilterMode === 'single') return suppliesDateFilterStart;
    return suppliesDateFilterStart === suppliesDateFilterEnd ? suppliesDateFilterStart : '';
  }, [suppliesDateFilterEnd, suppliesDateFilterMode, suppliesDateFilterStart]);

  const editableSuppliesCountByDate = useMemo(() => {
    const counts = new Map<string, number>();
    supplies.forEach(row => {
      const slotDate = getSupplySlotDateKey(row);
      if (!slotDate || isSupplyExecutionLocked(row)) return;
      counts.set(slotDate, (counts.get(slotDate) || 0) + 1);
    });
    return counts;
  }, [supplies]);

  const resolvedBulkTransportEditableCount = useMemo(() => {
    if (!resolvedBulkTransportDate) return 0;
    return editableSuppliesCountByDate.get(resolvedBulkTransportDate) || 0;
  }, [editableSuppliesCountByDate, resolvedBulkTransportDate]);

  const suppliesDateFilterLabel = useMemo(() => {
    if (suppliesDateFilterMode === 'single') {
      return formatDate(suppliesDateFilterStart);
    }
    return `${formatDate(suppliesDateFilterStart)} - ${formatDate(suppliesDateFilterEnd)}`;
  }, [suppliesDateFilterEnd, suppliesDateFilterMode, suppliesDateFilterStart]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [summaryRes, requestsRes, suppliesRes, readinessRes, diagnosticsRes, profilesRes, driversRes, vehiclesRes, recommendationsRes, sourceWarehousesRes, catalogRes, recomputeStatusRes] = await Promise.all([
        apiFetch('/api/ai/ozon-slot-requests/summary'),
        apiFetch('/api/ai/ozon-slot-requests?limit=20'),
        apiFetch('/api/ai/ozon-supplies?limit=200'),
        apiFetch('/api/ai/ozon-slot/runtime-readiness'),
        apiFetch('/api/ai/ozon-slot/worker-diagnostics?limit=30'),
        apiFetch('/api/ai/ozon-transport/profiles?limit=200'),
        apiFetch('/api/ai/ozon-transport/drivers?activeOnly=true'),
        apiFetch('/api/ai/ozon-transport/vehicles?activeOnly=true'),
        apiFetch('/api/ai/cluster-supply/recommendations?limit=500'),
        apiFetch('/api/ai/cluster-supply/source-warehouses'),
        apiFetch('/api/ai/cluster-supply/data'),
        apiFetch('/api/ai/cluster-supply/recompute-status'),
      ]);
      if (
        !summaryRes.ok ||
        !requestsRes.ok ||
        !suppliesRes.ok ||
        !readinessRes.ok ||
        !diagnosticsRes.ok ||
        !profilesRes.ok ||
        !driversRes.ok ||
        !vehiclesRes.ok ||
        !recommendationsRes.ok
      ) {
        throw new Error('Не удалось загрузить данные вкладки поставок');
      }
      const summaryPayload = (await summaryRes.json()) as SummaryPayload;
      const requestsPayload = (await requestsRes.json()) as { items?: SlotRequestRow[] };
      const suppliesPayload = (await suppliesRes.json()) as { items?: SupplyRow[] };
      const readinessPayload = (await readinessRes.json()) as SlotRuntimeReadinessPayload;
      const diagnosticsPayload = (await diagnosticsRes.json()) as SlotWorkerDiagnosticsPayload;
      const profilesPayload = (await profilesRes.json()) as { items?: TransportProfileRow[] };
      const driversPayload = (await driversRes.json()) as { items?: TransportDriverRow[] };
      const vehiclesPayload = (await vehiclesRes.json()) as { items?: TransportVehicleRow[] };
      const recommendationsPayload = (await recommendationsRes.json()) as ClusterSupplyRecommendation[];
      const sourceWarehousesPayload = sourceWarehousesRes.ok
        ? (await sourceWarehousesRes.json()) as SourceWarehouseResponse
        : { options: [] };
      const catalogPayload = catalogRes.ok
        ? (await catalogRes.json()) as ClusterSupplyCatalogResponse
        : { product_catalog: [] };
      const recomputePayload = recomputeStatusRes.ok
        ? (await recomputeStatusRes.json()) as BackgroundJobResponse
        : { status: 'idle', state: 'idle', job_id: null };
      const nextProfiles = Array.isArray(profilesPayload?.items) ? profilesPayload.items : [];
      setSummary(summaryPayload);
      setRequests(Array.isArray(requestsPayload?.items) ? requestsPayload.items.map(normalizeSlotRequestRow) : []);
      setSupplies(Array.isArray(suppliesPayload?.items) ? suppliesPayload.items.map(normalizeSupplyRow) : []);
      setRecommendations(Array.isArray(recommendationsPayload) ? recommendationsPayload : []);
      setRecommendationProductCatalog(Array.isArray(catalogPayload?.product_catalog) ? catalogPayload.product_catalog : []);
      setRecommendationWarehouseOptions(
        Array.isArray(sourceWarehousesPayload?.options)
          ? sourceWarehousesPayload.options.map(item => String(item || '').trim()).filter(Boolean)
          : [],
      );
      setRuntimeReadiness(readinessPayload);
      setWorkerDiagnostics(diagnosticsPayload);
      setProfiles(nextProfiles);
      setTransportDrivers(Array.isArray(driversPayload?.items) ? driversPayload.items : []);
      setTransportVehicles(Array.isArray(vehiclesPayload?.items) ? vehiclesPayload.items : []);
      setClusterSupplyRecomputeJob(recomputePayload);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Ошибка загрузки вкладки поставок');
    } finally {
      setLoading(false);
    }
  }, []);

  const applyLiveSnapshot = useCallback((payload: OzonSuppliesLiveSnapshotMessage) => {
    setSummary(payload.summary);
    setRequests(Array.isArray(payload.requests) ? payload.requests.map(normalizeSlotRequestRow) : []);
    setSupplies(Array.isArray(payload.supplies) ? payload.supplies.map(normalizeSupplyRow) : []);
  }, []);

  const isBackgroundJobActive = useCallback((job: BackgroundJobResponse | null | undefined) => {
    const status = String(job?.status || job?.state || '').trim().toLowerCase();
    if (status === 'running') return true;
    if (status !== 'queued') return false;
    const timestampRaw = String(job?.updated_at || job?.created_at || '').trim();
    if (!timestampRaw) return false;
    const queuedAt = Date.parse(timestampRaw);
    if (!Number.isFinite(queuedAt)) return false;
    return (Date.now() - queuedAt) <= 90_000;
  }, []);

  const clusterSupplyRecomputeActive = isBackgroundJobActive(clusterSupplyRecomputeJob);
  const clusterSupplyRecomputeMessage = String(
    clusterSupplyRecomputeJob?.progress?.message
    || clusterSupplyRecomputeJob?.error
    || '',
  ).trim();

  const triggerManualClusterSupplyRecompute = useCallback(async (): Promise<void> => {
    if (manualRecomputeBusy || clusterSupplyRecomputeActive) return;
    setManualRecomputeBusy(true);
    setError('');
    setFormNotice('');
    try {
      const res = await apiFetch('/api/ai/cluster-supply/recommendations/run-async', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trigger_source: 'manual_supplies_page',
          refresh_live_ozon_sync: true,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Не удалось запустить пересчёт рекомендаций.');
      }
      setFormNotice('Пересчёт рекомендаций запущен. Страница обновится автоматически.');
      // Refresh the recompute-status job snapshot so the active polling
      // loop (lines ~3336) picks up the new job and starts tracking.
      await load().catch(console.error);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось запустить пересчёт рекомендаций.');
    } finally {
      setManualRecomputeBusy(false);
    }
  }, [manualRecomputeBusy, clusterSupplyRecomputeActive, load]);

  const activeRecommendationPlanningKeys = useMemo(() => {
    const keys = new Set<string>();
    const today = toDateInputValue(new Date());
    const followupRequestIds = new Set<number>();
    supplies.forEach(supply => {
      if (!isSupplyFollowupRecommendationNeeded(supply)) return;
      const requestId = Number(supply.slot_request_id || 0);
      if (requestId > 0) {
        followupRequestIds.add(requestId);
      }
    });
    requests.forEach(request => {
      if (!isRequestBlockingRecommendation(request, today)) return;
      if (followupRequestIds.has(Number(request.id || 0))) return;
      request.items.forEach(item => {
        const key = recommendationPlanningKey(item.cluster_name, item.sku);
        if (key) keys.add(key);
      });
    });
    supplies.forEach(supply => {
      if (!isSupplyBlockingRecommendation(supply, today)) return;
      if (isSupplyFollowupRecommendationNeeded(supply)) return;
      supply.items.forEach(item => {
        const key = recommendationPlanningKey(supply.target_cluster, item.sku);
        if (key) keys.add(key);
      });
    });
    return keys;
  }, [requests, supplies]);

  const allActiveRecommendations = useMemo(() => {
    return recommendations.filter(item => (
      getRecommendationKind(item) === 'cluster_supply' && item.is_active !== false
    ));
  }, [recommendations]);

  const activeRecommendations = useMemo(() => {
    return recommendations.filter(item => {
      if (getRecommendationKind(item) !== 'cluster_supply' || item.is_active === false) {
        return false;
      }
      const key = recommendationPlanningKey(item.target_cluster, item.sku);
      return !key || !activeRecommendationPlanningKeys.has(key);
    });
  }, [activeRecommendationPlanningKeys, recommendations]);

  const recommendationProductBySku = useMemo(() => {
    const map = new Map<string, RecommendationProductOption>();
    recommendationProductCatalog.forEach(item => {
      const sku = String(item.sku || '').trim();
      if (sku) {
        map.set(sku, item);
      }
    });
    return map;
  }, [recommendationProductCatalog]);

  const recommendationArticleBySku = useCallback((skuRaw: string | null | undefined): string => {
    const sku = String(skuRaw || '').trim();
    if (!sku) return '—';
    const product = recommendationProductBySku.get(sku);
    const article = String(product?.product_alias || '').trim();
    if (article) return article;
    const displayName = String(product?.display_name || '').trim();
    if (displayName) return displayName;
    return sku;
  }, [recommendationProductBySku]);

  const buildRecommendationSummary = useCallback((items: ClusterSupplyRecommendation[]) => {
    const clusterSet = new Set<string>();
    const skuSet = new Set<string>();
    let totalQuantity = 0;
    const summaryBySize = new Map<string, number>();
    items.forEach(item => {
      const clusterName = String(item.target_cluster || '').trim();
      const sku = String(item.sku || '').trim();
      if (clusterName) clusterSet.add(clusterName);
      if (sku) skuSet.add(sku);
      totalQuantity += Number(item.recommended_qty || 0);
      const boxStats = buildBoxStats(item.boxes_plan_json);
      boxStats.bySize.forEach((count, boxSize) => {
        summaryBySize.set(boxSize, (summaryBySize.get(boxSize) || 0) + count);
      });
    });
    const totalBoxes = Array.from(summaryBySize.values()).reduce((acc, value) => acc + value, 0);
    return {
      clusterCount: clusterSet.size,
      skuCount: skuSet.size,
      totalQuantity,
      totalBoxes,
      boxLabel: formatBoxBreakdown(summaryBySize),
    };
  }, []);

  const recommendationSummary = useMemo(() => {
    return buildRecommendationSummary(activeRecommendations);
  }, [activeRecommendations, buildRecommendationSummary]);

  const recommendationPlanningSummary = useMemo(() => {
    return buildRecommendationSummary(activeRecommendations);
  }, [activeRecommendations, buildRecommendationSummary]);

  const recommendationWarehouseOptionsMerged = useMemo(() => {
    const options = new Set<string>();
    recommendationWarehouseOptions.forEach(item => {
      const warehouse = String(item || '').trim();
      if (warehouse) options.add(warehouse);
    });
    activeRecommendations.forEach(item => {
      const warehouse = String(item.source_warehouse || '').trim();
      if (warehouse) options.add(warehouse);
    });
    return Array.from(options).sort((left, right) => left.localeCompare(right, 'ru'));
  }, [activeRecommendations, recommendationWarehouseOptions]);

  const recommendationTasks = useMemo(() => {
    const grouped = new Map<string, RecommendationTask>();
    activeRecommendations.forEach(item => {
      const groupingKey = recommendationTaskMode === 'cluster'
        ? String(item.target_cluster || '').trim()
        : String(item.sku || '').trim();
      if (!groupingKey) return;
      const taskId = `${recommendationTaskMode}:${groupingKey}`;
      const clusterName = String(item.target_cluster || '').trim() || '—';
      const sku = String(item.sku || '').trim() || '—';
      const article = recommendationArticleBySku(item.sku);
      const productName = String(item.product_name || '').trim() || sku;
      const quantity = Number(item.recommended_qty || 0);
      const boxStats = buildBoxStats(item.boxes_plan_json);
      const sourceWarehouse = String(item.source_warehouse || '').trim();
      if (!grouped.has(taskId)) {
        grouped.set(taskId, {
          id: taskId,
          title: recommendationTaskMode === 'cluster' ? clusterName : article,
          subtitle: recommendationTaskMode === 'cluster' ? `${article} · SKU ${sku}` : `SKU ${sku}`,
          recommendationIds: [],
          sourceWarehouse,
          totalQuantity: 0,
          totalBoxes: 0,
          boxBySize: new Map<string, number>(),
          clusterCount: 0,
          skuCount: 0,
          priorityScore: 0,
          details: [],
        });
      }
      const task = grouped.get(taskId);
      if (!task) return;
      task.recommendationIds.push(item.id);
      if (!task.sourceWarehouse && sourceWarehouse) {
        task.sourceWarehouse = sourceWarehouse;
      }
      task.totalQuantity += quantity;
      task.totalBoxes += boxStats.totalBoxes;
      boxStats.bySize.forEach((count, boxSize) => {
        task.boxBySize.set(boxSize, (task.boxBySize.get(boxSize) || 0) + count);
      });
      task.priorityScore = Math.max(task.priorityScore, Number(item.priority_score || 0));
      task.details.push({
        recommendationId: item.id,
        clusterName,
        sku,
        article,
        productName,
        quantity,
        totalBoxes: boxStats.totalBoxes,
        boxBySize: boxStats.bySize,
        priorityScore: Number(item.priority_score || 0),
        shipByAt: item.ship_by_at ?? (item.meta_json && typeof item.meta_json.ship_by_at === 'string' ? item.meta_json.ship_by_at : null),
        etaAt: item.eta_at ?? (item.meta_json && typeof item.meta_json.eta_at === 'string' ? item.meta_json.eta_at : null),
        expectedRevenueSaved: item.expected_revenue_saved ?? asNumber(item.meta_json?.expected_revenue_saved) ?? null,
        expectedMarginSaved: item.expected_margin_saved ?? asNumber(item.meta_json?.expected_margin_saved) ?? null,
        metaJson: item.meta_json ?? null,
      });
    });
    return Array.from(grouped.values())
      .map(task => {
        const clusterSet = new Set<string>();
        const skuSet = new Set<string>();
        task.details.forEach(detail => {
          if (detail.clusterName && detail.clusterName !== '—') clusterSet.add(detail.clusterName);
          if (detail.sku && detail.sku !== '—') skuSet.add(detail.sku);
        });
        return {
          ...task,
          clusterCount: clusterSet.size,
          skuCount: skuSet.size,
          details: task.details.sort((left, right) => {
            if (right.priorityScore !== left.priorityScore) {
              return right.priorityScore - left.priorityScore;
            }
            const leftLabel = recommendationTaskMode === 'cluster'
              ? `${left.article} ${left.sku}`
              : left.clusterName;
            const rightLabel = recommendationTaskMode === 'cluster'
              ? `${right.article} ${right.sku}`
              : right.clusterName;
            return leftLabel.localeCompare(rightLabel, 'ru');
          }),
        };
      })
      .sort((left, right) => {
        if (right.priorityScore !== left.priorityScore) {
          return right.priorityScore - left.priorityScore;
        }
        return left.title.localeCompare(right.title, 'ru');
      });
  }, [activeRecommendations, recommendationArticleBySku, recommendationTaskMode]);

  const recommendationTasksSelection = useMemo(() => {
    return recommendationTasks.map(task => {
      const selectedDetails = task.details.filter(detail => !excludedRecommendationIds.includes(detail.recommendationId));
      const boxBySize = new Map<string, number>();
      let totalQuantity = 0;
      let totalBoxes = 0;
      let priorityScore = 0;
      const clusterSet = new Set<string>();
      const skuSet = new Set<string>();
      selectedDetails.forEach(detail => {
        const qtyOverride = recommendationQtyOverrides.get(detail.recommendationId);
        const boxOverride = recommendationBoxOverrides.get(detail.recommendationId);
        const effectiveQty = qtyOverride ?? detail.quantity;
        const effectiveBoxMap: Map<string, number> = boxOverride
          ? new Map(boxOverride.map(b => [b.box_size, b.boxes_used]))
          : detail.boxBySize;
        const effectiveTotalBoxes = boxOverride
          ? boxOverride.reduce((s, b) => s + b.boxes_used, 0)
          : detail.totalBoxes;

        totalQuantity += effectiveQty;
        totalBoxes += effectiveTotalBoxes;
        priorityScore = Math.max(priorityScore, detail.priorityScore);
        if (detail.clusterName && detail.clusterName !== '—') clusterSet.add(detail.clusterName);
        if (detail.sku && detail.sku !== '—') skuSet.add(detail.sku);
        effectiveBoxMap.forEach((count, boxSize) => {
          boxBySize.set(boxSize, (boxBySize.get(boxSize) || 0) + count);
        });
      });
      return {
        ...task,
        selectedDetails,
        selectedRecommendationIds: selectedDetails.map(detail => detail.recommendationId),
        selectedQuantity: totalQuantity,
        selectedBoxes: totalBoxes,
        selectedBoxBySize: boxBySize,
        selectedClusterCount: clusterSet.size,
        selectedSkuCount: skuSet.size,
        selectedPriorityScore: priorityScore,
        effectiveSourceWarehouse: recommendationSourceWarehouseOverrides[task.id] ?? task.sourceWarehouse,
      };
    });
  }, [excludedRecommendationIds, recommendationSourceWarehouseOverrides, recommendationTasks, recommendationQtyOverrides, recommendationBoxOverrides]);

  const selectedRecommendationTasks = useMemo(() => {
    return recommendationTasksSelection.filter(task => !disabledRecommendationTaskIds.includes(task.id) && task.selectedRecommendationIds.length > 0);
  }, [disabledRecommendationTaskIds, recommendationTasksSelection]);

  const selectedRecommendationBoxSummary = useMemo(() => {
    const bySize = new Map<string, number>();
    selectedRecommendationTasks.forEach(task => {
      task.selectedBoxBySize.forEach((count, boxSize) => {
        bySize.set(boxSize, (bySize.get(boxSize) || 0) + count);
      });
    });
    return bySize;
  }, [selectedRecommendationTasks]);

  const commonRecommendationWarehouse = useMemo(() => {
    const warehouses = recommendationTasksSelection
      .map(task => String(task.effectiveSourceWarehouse || '').trim())
      .filter(Boolean);
    if (warehouses.length === 0) return '';
    const unique = Array.from(new Set(warehouses));
    return unique.length === 1 ? unique[0] : '';
  }, [recommendationTasksSelection]);

  const openRecommendationsPlanner = useCallback(async () => {
    try {
      setError('');
      setRecommendationPlanningBusy(true);
      setDisabledRecommendationTaskIds([]);
      setExcludedRecommendationIds([]);
      setExpandedRecommendationTaskIds([]);
      setExpandedRecommendationReasonIds([]);
      setRecommendationQtyOverrides(new Map());
      setRecommendationBoxOverrides(new Map());
      setRecommendationTaskMode('cluster');
      setRecommendationWarehouseNotice('');
      setBulkRecommendationWarehouse(commonRecommendationWarehouse);
      setRecommendationsModalOpen(true);
    } catch (plannerError) {
      setError(plannerError instanceof Error ? plannerError.message : 'Ошибка открытия планирования');
    } finally {
      setRecommendationPlanningBusy(false);
    }
  }, [commonRecommendationWarehouse]);

  const resetForm = useCallback(() => {
    setEditingRequestId(null);
    setRequestType('by_product');
    setTitle('');
    setComment('');
    setSourceWarehouse('');
    setWindowStart('');
    setWindowEnd('');
    setAllowPartialBooking(true);
    setAutoCreateCargoPlaces(true);
    setItems([emptyItem()]);
    setFormNotice('');
  }, []);

  const closeRequestEditor = useCallback(() => {
    setRequestEditorOpen(false);
    resetForm();
  }, [resetForm]);

  const openNewRequestEditor = useCallback(() => {
    resetForm();
    setRequestEditorOpen(true);
  }, [resetForm]);

  const populateFormFromRequest = useCallback((row: SlotRequestRow) => {
    setEditingRequestId(row.id);
    setRequestType((row.request_type === 'by_cluster' ? 'by_cluster' : 'by_product'));
    setTitle(row.title || '');
    setComment(row.comment || '');
    setSourceWarehouse(row.source_warehouse || '');
    setWindowStart(row.slot_window_start_date || '');
    setWindowEnd(row.slot_window_end_date || '');
    setAllowPartialBooking(Boolean(row.allow_partial_booking));
    setAutoCreateCargoPlaces(
      typeof row.auto_create_cargo_places === 'boolean'
        ? row.auto_create_cargo_places
        : Boolean(row.meta_json?.auto_create_cargo_places ?? true),
    );
    setItems(
      row.items.length > 0
        ? row.items.map(item => ({
            cluster_name: item.cluster_name || '',
            sku: item.sku || '',
            product_name: item.product_name || '',
            quantity: item.quantity ? String(item.quantity) : '',
          }))
        : [emptyItem()],
    );
    setFormNotice('');
    setRequestEditorOpen(true);
  }, []);

  const saveRequest = useCallback(async () => {
    try {
      setFormBusy(true);
      setError('');
      setFormNotice('');
      const payload = {
        request_type: requestType,
        source_type: 'manual',
        title,
        comment,
        source_warehouse: sourceWarehouse,
        slot_window_start_date: windowStart || null,
        slot_window_end_date: windowEnd || null,
        allow_partial_booking: allowPartialBooking,
        auto_create_cargo_places: autoCreateCargoPlaces,
        items: items
          .filter(item => item.cluster_name.trim() && item.sku.trim() && item.quantity.trim())
          .map((item, index) => ({
            cluster_name: item.cluster_name.trim(),
            sku: item.sku.trim(),
            product_name: item.product_name.trim(),
            quantity: item.quantity.trim(),
            priority_rank: index + 1,
          })),
      };
      const endpoint = editingRequestId ? `/api/ai/ozon-slot-requests/${editingRequestId}` : '/api/ai/ozon-slot-requests';
      const method = editingRequestId ? 'PUT' : 'POST';
      const res = await apiFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let detail = 'Не удалось сохранить заявку';
        try {
          const errorPayload = await res.json();
          detail = String(errorPayload?.detail || detail);
        } catch {
          // ignore
        }
        throw new Error(detail);
      }
      const successMessage = editingRequestId ? 'Заявка обновлена.' : 'Черновик заявки создан.';
      resetForm();
      setRequestEditorOpen(false);
      await load();
      setFormNotice(successMessage);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Ошибка сохранения заявки');
    } finally {
      setFormBusy(false);
    }
  }, [
    allowPartialBooking,
    autoCreateCargoPlaces,
    comment,
    editingRequestId,
    items,
    load,
    requestType,
    resetForm,
    sourceWarehouse,
    title,
    windowEnd,
    windowStart,
  ]);

  const updateRequestStatus = useCallback(async (requestId: number, action: 'start_search' | 'stop_search' | 'cancel') => {
    try {
      setFormBusy(true);
      setError('');
      setFormNotice('');
      if (action === 'start_search') {
        const draftRes = await apiFetch(`/api/ai/ozon-slot-requests/${requestId}/draft-supplies/async`, {
          method: 'POST',
        });
        if (!draftRes.ok) {
          let detail = 'Не удалось поставить создание draft-поставок в очередь';
          try {
            const errorPayload = await draftRes.json();
            detail = String(errorPayload?.detail || detail);
          } catch {
            // ignore
          }
          throw new Error(detail);
        }
        const draftPayload = (await draftRes.json()) as { job_id?: string };
        const draftJobId = String(draftPayload?.job_id || '').trim();
        if (!draftJobId) {
          throw new Error('Фоновая задача создания draft-поставок не вернула job_id');
        }
        const searchRes = await apiFetch('/api/ai/ozon-slot/search-after-drafts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            request_ids: [requestId],
            wait_for_job_ids: [draftJobId],
            draft_dependency_items: [{ request_id: requestId, job_id: draftJobId }],
            force_run: true,
          }),
        });
        if (!searchRes.ok) {
          let detail = 'Не удалось поставить поиск слотов в очередь';
          try {
            const errorPayload = await searchRes.json();
            detail = String(errorPayload?.detail || detail);
          } catch {
            // ignore
          }
          throw new Error(detail);
        }
        setFormNotice('Создание draft-поставок и последующий поиск слотов поставлены в очередь.');
        await load();
        return;
      }
      const res = await apiFetch(`/api/ai/ozon-slot-requests/${requestId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (!res.ok) {
        let detail = 'Не удалось обновить статус заявки';
        try {
          const errorPayload = await res.json();
          detail = String(errorPayload?.detail || detail);
        } catch {
          // ignore
        }
        throw new Error(detail);
      }
      setFormNotice(
        action === 'stop_search'
          ? 'Поиск слота остановлен.'
          : 'Заявка отменена.',
      );
      await load();
    } catch (statusError) {
      setError(statusError instanceof Error ? statusError.message : 'Ошибка обновления статуса');
    } finally {
      setFormBusy(false);
    }
  }, [load]);

  const runBulkByDate = useCallback(async () => {
    try {
      if (!bulkDate) {
        setError('Укажите дату для массовой операции.');
        return;
      }
      setFormBusy(true);
      setError('');
      setFormNotice('');
      const res = await apiFetch('/api/ai/ozon-slot-requests/bulk-by-date', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: bulkDate,
          date_field: bulkDateField,
          action: bulkAction,
          max_requests: 300,
        }),
      });
      if (!res.ok) {
        let detail = 'Не удалось выполнить массовую операцию';
        try {
          const errorPayload = await res.json();
          detail = String(errorPayload?.detail || detail);
        } catch {
          // ignore
        }
        throw new Error(detail);
      }
      const payload = (await res.json()) as {
        matched?: number;
        processed?: number;
        skipped?: number;
      };
      setFormNotice(
        `Массовая операция выполнена: найдено ${payload.matched ?? 0}, обработано ${payload.processed ?? 0}, пропущено ${payload.skipped ?? 0}.`,
      );
      await load();
    } catch (bulkError) {
      setError(bulkError instanceof Error ? bulkError.message : 'Ошибка массовой операции');
    } finally {
      setFormBusy(false);
    }
  }, [bulkAction, bulkDate, bulkDateField, load]);

  const fillSupplyCargoPlaces = useCallback(async (supplyId: number) => {
    try {
      const supply = supplies.find(row => row.id === supplyId) || null;
      if (supply && isSupplyExecutionLocked(supply)) {
        setError(`Поставка #${supplyId} уже на этапе "${getEffectiveSupplyStatusLabel(supply)}": создание грузомест недоступно.`);
        return;
      }
      setFormBusy(true);
      setError('');
      setFormNotice('');
      const res = await apiFetch(`/api/ai/ozon-supplies/${supplyId}/cargo-places/fill-async`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clear_existing: true }),
      });
      if (!res.ok) {
        let detail = 'Не удалось поставить создание грузомест в очередь';
        try {
          const errorPayload = await res.json();
          detail = String(errorPayload?.detail || detail);
        } catch {
          // ignore
        }
        throw new Error(detail);
      }
      const payload = (await res.json()) as {
        job_id?: string;
        reused?: boolean;
      };
      setFormNotice(
        `Поставка #${supplyId}: создание грузомест поставлено в очередь${payload?.job_id ? `, job ${payload.job_id}` : ''}${payload?.reused ? ' (reuse)' : ''}.`,
      );
      await load();
    } catch (fillError) {
      setError(fillError instanceof Error ? fillError.message : 'Ошибка постановки создания грузомест в очередь');
    } finally {
      setFormBusy(false);
    }
  }, [load, supplies]);

  const fillCargoPlacesByDate = useCallback(async (targetDate?: string) => {
    try {
      const resolvedDate = String(targetDate || cargoFillDate || '').trim();
      if (!resolvedDate) {
        setError('Укажите дату поставки для заполнения грузомест.');
        return;
      }
      if ((editableSuppliesCountByDate.get(resolvedDate) || 0) <= 0) {
        setError(`На дату ${resolvedDate} нет поставок, где ещё можно создавать грузоместа.`);
        return;
      }
      setFormBusy(true);
      setError('');
      setFormNotice('');
      const res = await apiFetch('/api/ai/ozon-supplies/cargo-places/fill-by-date', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: resolvedDate,
          clear_existing: true,
          max_supplies: 300,
        }),
      });
      if (!res.ok) {
        let detail = 'Не удалось заполнить грузоместа по дате';
        try {
          const errorPayload = await res.json();
          detail = String(errorPayload?.detail || detail);
        } catch {
          // ignore
        }
        throw new Error(detail);
      }
      const payload = (await res.json()) as {
        matched?: number;
        processed?: number;
        failed?: number;
        created_cargo_places?: number;
        complete_supplies?: number;
        incomplete_supplies?: number;
      };
      setFormNotice(
        `Заполнение по дате ${resolvedDate}: найдено ${payload?.matched ?? 0}, обработано ${payload?.processed ?? 0}, ошибок ${payload?.failed ?? 0}, создано грузомест ${payload?.created_cargo_places ?? 0}, полных поставок ${payload?.complete_supplies ?? 0}, неполных ${payload?.incomplete_supplies ?? 0}.`,
      );
      await load();
    } catch (bulkFillError) {
      setError(bulkFillError instanceof Error ? bulkFillError.message : 'Ошибка массового заполнения грузомест');
    } finally {
      setFormBusy(false);
    }
  }, [cargoFillDate, editableSuppliesCountByDate, load]);

  const downloadSupplyRegistry = useCallback(async ({
    targetDate,
    dateFrom,
    dateTo,
  }: {
    targetDate?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    try {
      const resolvedDate = String(targetDate || cargoFillDate || '').trim();
      const resolvedDateFrom = String(dateFrom || '').trim();
      const resolvedDateTo = String(dateTo || '').trim();
      const isRange = Boolean(resolvedDateFrom && resolvedDateTo);
      if (!resolvedDate && !isRange) {
        setError('Укажите дату или диапазон дат для PDF-реестра.');
        return;
      }
      setFormBusy(true);
      setError('');
      setFormNotice('');
      const query = isRange
        ? `date_from=${encodeURIComponent(resolvedDateFrom)}&date_to=${encodeURIComponent(resolvedDateTo)}`
        : `date=${encodeURIComponent(resolvedDate)}`;
      const res = await apiFetch(`/api/ai/ozon-supplies/report-by-date?${query}`);
      if (!res.ok) {
        let detail = 'Не удалось сформировать PDF-реестр';
        try {
          const errorPayload = await res.json();
          detail = String(errorPayload?.detail || detail);
        } catch {
          const text = await res.text();
          if (text) detail = text;
        }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const contentDisposition = String(res.headers.get('Content-Disposition') || '');
      const match = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
      const fileName = match?.[1] || (
        isRange
          ? `ozon-supply-registry-${resolvedDateFrom}_${resolvedDateTo}.pdf`
          : `ozon-supply-registry-${resolvedDate}.pdf`
      );
      downloadBlobFile(blob, fileName);
      setFormNotice(
        isRange
          ? `PDF-реестр за период ${resolvedDateFrom} — ${resolvedDateTo} сформирован.`
          : `PDF-реестр по дате ${resolvedDate} сформирован.`,
      );
    } catch (reportError) {
      setError(reportError instanceof Error ? reportError.message : 'Ошибка формирования PDF-реестра');
    } finally {
      setFormBusy(false);
    }
  }, [cargoFillDate]);

  const openSupplyDateAction = useCallback((mode: SupplyDateActionMode) => {
    const suggestedDate = mode === 'cargo_fill' ? nearestCargoFillDate : nearestRegistryDate;
    const fallbackDate = suggestedDate || cargoFillDate || defaultSupplyToday;
    if (mode === 'registry' && activeShipmentPeriodStartDate) {
      setSupplyDateActionDraft(activeShipmentPeriodStartDate);
      setSupplyDateActionDraftEnd(fallbackDate);
      setSupplyDateActionDraftMode('range');
    } else {
      setSupplyDateActionDraft(fallbackDate);
      setSupplyDateActionDraftEnd(fallbackDate);
      setSupplyDateActionDraftMode('single');
    }
    setSupplyDateActionMode(mode);
  }, [activeShipmentPeriodStartDate, cargoFillDate, defaultSupplyToday, nearestCargoFillDate, nearestRegistryDate]);

  const closeSupplyDateAction = useCallback(() => {
    if (formBusy) return;
    setSupplyDateActionMode(null);
  }, [formBusy]);

  const submitSupplyDateAction = useCallback(async () => {
    const resolvedDate = String(supplyDateActionDraft || '').trim();
    const resolvedDateEnd = String(supplyDateActionDraftEnd || '').trim();
    if (!resolvedDate || !supplyDateActionMode) {
      setError('Укажите дату.');
      return;
    }
    setCargoFillDate(resolvedDate);
    if (supplyDateActionMode === 'cargo_fill') {
      await fillCargoPlacesByDate(resolvedDate);
    } else {
      if (supplyDateActionDraftMode === 'range') {
        if (!resolvedDateEnd) {
          setError('Укажите конечную дату диапазона.');
          return;
        }
        await downloadSupplyRegistry({ dateFrom: resolvedDate, dateTo: resolvedDateEnd });
      } else {
        await downloadSupplyRegistry({ targetDate: resolvedDate });
      }
    }
    setSupplyDateActionMode(null);
  }, [downloadSupplyRegistry, fillCargoPlacesByDate, supplyDateActionDraft, supplyDateActionDraftEnd, supplyDateActionDraftMode, supplyDateActionMode]);

  const openTransportCacheEditor = useCallback(() => {
    setTransportCacheEditorOpen(true);
  }, []);

  const closeTransportEditor = useCallback(() => {
    setTransportCacheEditorOpen(false);
  }, []);

  const applyTransportHistoryProfile = useCallback((profile: TransportHistoryProfile) => {
    setTransportEditorDriverName(profile.driverName);
    setTransportEditorDriverPhone(profile.driverPhone);
    setTransportEditorVehicleBrand(profile.vehicleBrand);
    setTransportEditorVehiclePlate(profile.vehiclePlate);
  }, []);

  const saveTransportCache = useCallback(() => {
    const fullName = transportEditorDriverName.trim();
    const phone = transportEditorDriverPhone.trim();
    const brand = transportEditorVehicleBrand.trim();
    const licensePlate = transportEditorVehiclePlate.trim();
    if (!fullName || !phone || !brand || !licensePlate) {
      setError('Заполните ФИО, телефон, марку и номер ТС.');
      return;
    }
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(transportCacheStorageKey, JSON.stringify({
          driverName: fullName,
          driverPhone: phone,
          vehicleBrand: brand,
          vehiclePlate: licensePlate,
        }));
      }
      setError('');
      setFormNotice('Кеш ТС и водителя обновлен.');
      setTransportCacheEditorOpen(false);
    } catch {
      setError('Не удалось сохранить кеш ТС/водителя');
    }
  }, [
    transportEditorDriverName,
    transportEditorDriverPhone,
    transportEditorVehicleBrand,
    transportEditorVehiclePlate,
  ]);

  const assignCachedTransportForSupply = useCallback(async (supply: SupplyRow) => {
    try {
      if (isSupplyExecutionLocked(supply)) {
        setError(`Поставка #${supply.id} уже на этапе "${getEffectiveSupplyStatusLabel(supply)}": назначение ТС и водителя недоступно.`);
        return;
      }
      const fullName = transportEditorDriverName.trim();
      const phone = transportEditorDriverPhone.trim();
      const brand = transportEditorVehicleBrand.trim();
      const licensePlate = transportEditorVehiclePlate.trim();
      if (!fullName || !phone || !brand || !licensePlate) {
        setError('Сначала заполните кеш ТС и водителя.');
        setTransportCacheEditorOpen(true);
        return;
      }
      setTransportAssigningSupplyIds(current => (
        current.includes(supply.id) ? current : [...current, supply.id]
      ));
      setError('');
      setFormNotice('');
      const res = await apiFetch(`/api/ai/ozon-supplies/${supply.id}/transport-assignment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignment_status: 'assigned',
          vehicle: {
            brand,
            license_plate: licensePlate,
          },
          driver: {
            full_name: fullName,
            phone,
          },
        }),
      });
      if (!res.ok) {
        let detail = 'Не удалось назначить ТС и водителя';
        try {
          const errorPayload = await res.json();
          detail = String(errorPayload?.detail || detail);
        } catch {
          // ignore
        }
        throw new Error(detail);
      }
      setFormNotice(`Поставка #${supply.id}: ТС и водитель назначены.`);
      await load();
    } catch (assignError) {
      setError(assignError instanceof Error ? assignError.message : 'Ошибка назначения ТС/водителя');
    } finally {
      setTransportAssigningSupplyIds(current => current.filter(item => item !== supply.id));
    }
  }, [
    load,
    transportEditorDriverName,
    transportEditorDriverPhone,
    transportEditorVehicleBrand,
    transportEditorVehiclePlate,
  ]);

  const cancelSupply = useCallback(async (supply: SupplyRow) => {
    try {
      if (!isSupplyCancelable(supply)) {
        setError(`Поставка #${supply.id} уже нельзя отменить на этапе "${getEffectiveSupplyStatusLabel(supply)}".`);
        return;
      }
      if (!window.confirm(`Отменить поставку #${supply.id} в OZON? Локальный статус изменится только после подтверждения OZON.`)) {
        return;
      }
      setSupplyCancellingIds(current => (
        current.includes(supply.id) ? current : [...current, supply.id]
      ));
      setError('');
      setFormNotice('');
      const res = await apiFetch(`/api/ai/ozon-supplies/${supply.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        let detail = 'Не удалось отменить поставку в OZON';
        try {
          const errorPayload = await res.json();
          detail = String(errorPayload?.detail || detail);
        } catch {
          // ignore
        }
        throw new Error(detail);
      }
      const payload = (await res.json()) as {
        operation_id?: string | null;
      };
      setFormNotice(
        `Поставка #${supply.id} отменена в OZON${payload?.operation_id ? `, operation ${payload.operation_id}` : ''}.`,
      );
      await load();
    } catch (cancelError) {
      setError(cancelError instanceof Error ? cancelError.message : 'Ошибка отмены поставки');
    } finally {
      setSupplyCancellingIds(current => current.filter(item => item !== supply.id));
    }
  }, [load]);

  const openSuppliesDateFilter = useCallback(() => {
    setSuppliesDateFilterDraftMode(suppliesDateFilterMode);
    setSuppliesDateFilterDraftStart(suppliesDateFilterStart);
    setSuppliesDateFilterDraftEnd(suppliesDateFilterEnd);
    setSuppliesDateFilterOpen(true);
  }, [suppliesDateFilterEnd, suppliesDateFilterMode, suppliesDateFilterStart]);

  const applySuppliesDateFilter = useCallback(() => {
    const nextStart = suppliesDateFilterDraftStart;
    const nextEnd = suppliesDateFilterDraftMode === 'single' ? suppliesDateFilterDraftStart : suppliesDateFilterDraftEnd;
    setSuppliesDateFilterMode(suppliesDateFilterDraftMode);
    setSuppliesDateFilterStart(nextStart);
    setSuppliesDateFilterEnd(nextEnd);
    setSuppliesDateFilterOpen(false);
  }, [suppliesDateFilterDraftEnd, suppliesDateFilterDraftMode, suppliesDateFilterDraftStart]);

  const resetSuppliesDateFilterToWeek = useCallback(() => {
    const range = getCurrentWeekRange();
    setSuppliesDateFilterDraftMode('range');
    setSuppliesDateFilterDraftStart(range.start);
    setSuppliesDateFilterDraftEnd(range.end);
  }, []);

  const setSuppliesDateFilterToToday = useCallback(() => {
    const today = toDateInputValue(new Date());
    setSuppliesDateFilterDraftMode('single');
    setSuppliesDateFilterDraftStart(today);
    setSuppliesDateFilterDraftEnd(today);
  }, []);

  const assignTransportByDate = useCallback(async (
    targetDate?: string,
    transportOverride?: {
      fullName?: string;
      phone?: string;
      brand?: string;
      licensePlate?: string;
    },
  ) => {
    try {
      const effectiveDate = String(targetDate || '').trim();
      if (!effectiveDate) {
        setError('Укажите дату слота для массового назначения ТС/водителя.');
        return;
      }
      if ((editableSuppliesCountByDate.get(effectiveDate) || 0) <= 0) {
        setError(`На дату ${effectiveDate} нет поставок, где ещё можно назначать ТС и водителя.`);
        return;
      }
      const fullName = String(transportOverride?.fullName || '').trim();
      const phone = String(transportOverride?.phone || '').trim();
      const brand = String(transportOverride?.brand || '').trim();
      const licensePlate = String(transportOverride?.licensePlate || '').trim();
      if (!fullName || !phone || !brand || !licensePlate) {
        setError('Заполните ФИО, телефон, марку и номер ТС для массового назначения.');
        return;
      }
      setFormBusy(true);
      setError('');
      setFormNotice('');
      const res = await apiFetch('/api/ai/ozon-supplies/transport-assignment/by-date', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: effectiveDate,
          vehicle: {
            brand,
            license_plate: licensePlate,
          },
          driver: {
            full_name: fullName,
            phone,
          },
          skip_if_same: true,
          max_supplies: 500,
        }),
      });
      if (!res.ok) {
        let detail = 'Не удалось массово назначить ТС/водителя';
        try {
          const errorPayload = await res.json();
          detail = String(errorPayload?.detail || detail);
        } catch {
          // ignore
        }
        throw new Error(detail);
      }
      const payload = (await res.json()) as {
        matched?: number;
        processed?: number;
        skipped_same?: number;
        skipped_assigned?: number;
      };
      setFormNotice(
        `Назначение по дате ${effectiveDate}: найдено ${payload?.matched ?? 0}, назначено ${payload?.processed ?? 0}, без изменений ${payload?.skipped_same ?? 0}, уже назначены ${payload?.skipped_assigned ?? 0}.`,
      );
      await load();
    } catch (assignError) {
      setError(assignError instanceof Error ? assignError.message : 'Ошибка массового назначения ТС/водителя');
    } finally {
      setFormBusy(false);
    }
  }, [editableSuppliesCountByDate, load]);

  const assignTransportByFilteredDate = useCallback(async () => {
    if (!resolvedBulkTransportDate) {
      setError('Для массового назначения выберите одну дату в фильтре блока "Поставки".');
      return;
    }
    await assignTransportByDate(resolvedBulkTransportDate, {
      fullName: transportEditorDriverName,
      phone: transportEditorDriverPhone,
      brand: transportEditorVehicleBrand,
      licensePlate: transportEditorVehiclePlate,
    });
  }, [
    assignTransportByDate,
    resolvedBulkTransportDate,
    transportEditorDriverName,
    transportEditorDriverPhone,
    transportEditorVehicleBrand,
    transportEditorVehiclePlate,
  ]);

  const toggleRecommendationTaskExpanded = useCallback((taskId: string) => {
    setExpandedRecommendationTaskIds(current => (
      current.includes(taskId)
        ? current.filter(item => item !== taskId)
        : [...current, taskId]
    ));
  }, []);

  const toggleRecommendationReasonExpanded = useCallback((recommendationId: number) => {
    setExpandedRecommendationReasonIds(current => (
      current.includes(recommendationId)
        ? current.filter(item => item !== recommendationId)
        : [...current, recommendationId]
    ));
  }, []);

  const applyBulkRecommendationWarehouse = useCallback((nextWarehouseRaw: string) => {
    const nextWarehouse = nextWarehouseRaw.trim();
    const previousWarehouse = bulkRecommendationWarehouse.trim();
    const targetTasks = recommendationTasksSelection.filter(task => (
      previousWarehouse ? String(task.effectiveSourceWarehouse || '').trim() === previousWarehouse : true
    ));
    let changed = 0;
    setRecommendationSourceWarehouseOverrides(current => {
      const next = { ...current };
      targetTasks.forEach(task => {
        const currentWarehouse = String(task.effectiveSourceWarehouse || '').trim();
        if (currentWarehouse === nextWarehouse) return;
        next[task.id] = nextWarehouse;
        changed += 1;
      });
      return next;
    });
    setBulkRecommendationWarehouse(nextWarehouse);
    setRecommendationWarehouseNotice(`Склад изменен успешно на ${changed}/${targetTasks.length} задачах.`);
  }, [bulkRecommendationWarehouse, recommendationTasksSelection]);

  const toggleRecommendationTaskDisabled = useCallback((taskId: string) => {
    setDisabledRecommendationTaskIds(current => (
      current.includes(taskId)
        ? current.filter(item => item !== taskId)
        : [...current, taskId]
    ));
  }, []);

  const toggleRecommendationDetailExcluded = useCallback((recommendationId: number) => {
    setExcludedRecommendationIds(current => (
      current.includes(recommendationId)
        ? current.filter(item => item !== recommendationId)
        : [...current, recommendationId]
    ));
  }, []);

  // --- Quantity / box override handlers ---
  const replanBoxesDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleQtyOverride = useCallback((recId: number, rawQty: number, detail: RecommendationTaskDetail) => {
    const qty = Math.max(1, Math.round(rawQty || 0));
    setRecommendationQtyOverrides(prev => {
      const next = new Map(prev);
      if (qty === detail.quantity) {
        next.delete(recId);
      } else {
        next.set(recId, qty);
      }
      return next;
    });
    // Debounced replan boxes via API
    if (replanBoxesDebounceRef.current) clearTimeout(replanBoxesDebounceRef.current);
    replanBoxesDebounceRef.current = setTimeout(async () => {
      try {
        const res = await apiFetch('/api/ai/cluster-supply/replan-boxes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sku: detail.sku, target_qty: qty }),
        });
        if (res.ok) {
          const data = await res.json();
          const boxPlan: ClusterSupplyRecommendationBoxPlan[] = (data.box_plan || []).map((b: Record<string, unknown>) => ({
            box_size: String(b.box_size || ''),
            boxes_used: Number(b.boxes_used || 0),
            units_per_box: Number(b.units_per_box || 0),
            planned_units: Number(b.planned_units || 0),
          }));
          setRecommendationBoxOverrides(prev => {
            const next = new Map(prev);
            if (boxPlan.length > 0) {
              next.set(recId, boxPlan);
            } else {
              next.delete(recId);
            }
            return next;
          });
        }
      } catch {
        // API failure — keep manual qty, don't update boxes
      }
    }, 500);
  }, []);

  const handleBoxChange = useCallback((recId: number, boxSize: string, delta: number, detail: RecommendationTaskDetail) => {
    setRecommendationBoxOverrides(prev => {
      const next = new Map(prev);
      const originalPlan: ClusterSupplyRecommendationBoxPlan[] = prev.get(recId)
        || (detail.metaJson?.boxes_plan_json as ClusterSupplyRecommendationBoxPlan[] | undefined)
        || Array.from(detail.boxBySize.entries()).map(([size, count]) => ({
          box_size: size,
          boxes_used: count,
          units_per_box: 0,
          planned_units: 0,
        }));

      const updated = originalPlan.map(box => {
        if (box.box_size !== boxSize) return box;
        const newCount = Math.max(0, box.boxes_used + delta);
        return {
          ...box,
          boxes_used: newCount,
          planned_units: newCount * box.units_per_box,
        };
      });
      next.set(recId, updated);

      // Sync qty override from box plan total
      const totalUnits = updated.reduce((sum, b) => sum + b.planned_units, 0);
      setRecommendationQtyOverrides(qPrev => {
        const qNext = new Map(qPrev);
        if (totalUnits > 0 && totalUnits !== detail.quantity) {
          qNext.set(recId, totalUnits);
        } else {
          qNext.delete(recId);
        }
        return qNext;
      });

      return next;
    });
  }, []);

  const updateRecommendationTaskWarehouse = useCallback((taskId: string, warehouse: string) => {
    setRecommendationSourceWarehouseOverrides(current => ({
      ...current,
      [taskId]: warehouse,
    }));
  }, []);

  const createRecommendationRequests = useCallback(async () => {
    const selectedTasks = selectedRecommendationTasks;
    if (!recommendationWindowStart || !recommendationWindowEnd) {
      setError('Укажите период поиска для рекомендаций.');
      return;
    }
    if (recommendationWindowEnd < recommendationWindowStart) {
      setError('Дата конца периода не может быть раньше даты начала.');
      return;
    }
    if (selectedTasks.length === 0) {
      setError('Выберите хотя бы одну задачу для создания заявок.');
      return;
    }
    try {
      setRecommendationSubmitBusy(true);
      setError('');
      setFormNotice('');
      let createdRequests = 0;
      let reusedRequests = 0;
      const requestIds: number[] = [];
      const draftJobIds: string[] = [];
      const draftDependencyItems: Array<{ request_id: number; job_id: string }> = [];

      for (const task of selectedTasks) {
        const createRes = await apiFetch('/api/ai/ozon-slot-requests/from-recommendation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cluster_recommendation_ids: task.selectedRecommendationIds,
            title: `AI-заявка: ${task.title}`,
            comment: `Создано из блока рекомендаций OZON (${recommendationTaskMode === 'cluster' ? 'группировка по кластеру' : 'группировка по SKU'}).`,
            source_warehouse: task.effectiveSourceWarehouse || null,
            slot_window_start_date: recommendationWindowStart,
            slot_window_end_date: recommendationWindowEnd,
            tracking_enabled: recommendationTrackingEnabled,
            auto_create_cargo_places: true,
            quantity_overrides: Object.fromEntries(
              task.selectedRecommendationIds
                .filter((id: number) => recommendationQtyOverrides.has(id))
                .map((id: number) => [id, recommendationQtyOverrides.get(id)])
            ),
          }),
        });
        if (!createRes.ok) {
          let detail = `Не удалось создать заявку для "${task.title}"`;
          try {
            const payload = await createRes.json();
            detail = String(payload?.detail || detail);
          } catch {
            // ignore
          }
          throw new Error(detail);
        }
        const requestPayload = (await createRes.json()) as SlotRequestRow & { reused_existing?: boolean };
        const requestId = Number(requestPayload.id || 0);
        if (!Number.isFinite(requestId) || requestId <= 0) {
          throw new Error(`API не вернул id заявки для "${task.title}"`);
        }
        requestIds.push(requestId);
        if (requestPayload.reused_existing) {
          reusedRequests += 1;
        } else {
          createdRequests += 1;
        }

        const draftRes = await apiFetch(`/api/ai/ozon-slot-requests/${requestId}/draft-supplies/async`, {
          method: 'POST',
        });
        if (!draftRes.ok) {
          let detail = `Не удалось создать draft-поставки для "${task.title}"`;
          try {
            const payload = await draftRes.json();
            detail = String(payload?.detail || detail);
          } catch {
            // ignore
          }
          throw new Error(detail);
        }
        const draftPayload = (await draftRes.json()) as { job_id?: string };
        const jobId = String(draftPayload?.job_id || '').trim();
        if (!jobId) {
          throw new Error(`Фоновая задача draft-поставок не вернула job_id для "${task.title}"`);
        }
        draftJobIds.push(jobId);
        draftDependencyItems.push({ request_id: requestId, job_id: jobId });
      }

      const searchRes = await apiFetch('/api/ai/ozon-slot/search-after-drafts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_ids: requestIds,
          wait_for_job_ids: draftJobIds,
          draft_dependency_items: draftDependencyItems,
          force_run: true,
        }),
      });
      if (!searchRes.ok) {
        let detail = 'Не удалось поставить поиск слотов в очередь после создания draft-поставок';
        try {
          const payload = await searchRes.json();
          detail = String(payload?.detail || detail);
        } catch {
          // ignore
        }
        throw new Error(detail);
      }

      setRecommendationsModalOpen(false);
      setFormNotice(
        `Заявки обработаны: создано ${createdRequests}, переиспользовано ${reusedRequests}, создание draft-поставок и последующий поиск слотов поставлены в очередь.`,
      );
      await load();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Не удалось создать заявки по рекомендациям');
    } finally {
      setRecommendationSubmitBusy(false);
    }
  }, [
    load,
    recommendationTaskMode,
    recommendationTrackingEnabled,
    recommendationWindowEnd,
    recommendationWindowStart,
    selectedRecommendationTasks,
  ]);

  const openAudit = useCallback(async (requestId: number) => {
    try {
      setAuditLoading(true);
      setSelectedAuditRequestId(requestId);
      setSelectedAuditSupplyId(null);
      setSelectedAuditSupplyDetail(null);
      setAuditModalTitle(`Информация по заявке #${requestId}`);
      const res = await apiFetch(`/api/ai/ozon-slot-requests/${requestId}/events?limit=200`);
      if (!res.ok) {
        let detail = 'Не удалось загрузить аудит заявки';
        try {
          const errorPayload = await res.json();
          detail = String(errorPayload?.detail || detail);
        } catch {
          // ignore
        }
        throw new Error(detail);
      }
      const payload = (await res.json()) as { items?: SlotRequestEventRow[] };
      setAuditEvents(Array.isArray(payload?.items) ? payload.items : []);
    } catch (auditError) {
      setError(auditError instanceof Error ? auditError.message : 'Ошибка загрузки аудита');
      setSelectedAuditRequestId(null);
      setAuditEvents([]);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const openSupplyInfo = useCallback(async (supply: SupplyRow) => {
    const requestId = Number(supply.slot_request_id || 0);
    try {
      setAuditLoading(true);
      setError('');
      setSelectedAuditRequestId(Number.isFinite(requestId) && requestId > 0 ? requestId : null);
      setSelectedAuditSupplyId(supply.id);
      setSelectedAuditSupplyDetail(null);
      setAuditModalTitle(`Информация по поставке #${supply.id}`);
      const supplyRes = await apiFetch(`/api/ai/ozon-supplies/${supply.id}`);
      if (!supplyRes.ok) {
        let detail = 'Не удалось загрузить детали поставки';
        try {
          const errorPayload = await supplyRes.json();
          detail = String(errorPayload?.detail || detail);
        } catch {
          // ignore
        }
        throw new Error(detail);
      }
      const supplyPayload = normalizeSupplyRow((await supplyRes.json()) as SupplyRow);
      setSelectedAuditSupplyDetail(supplyPayload);
      if (Number.isFinite(requestId) && requestId > 0) {
        const res = await apiFetch(`/api/ai/ozon-slot-requests/${requestId}/events?limit=200`);
        if (res.ok) {
          const payload = (await res.json()) as { items?: SlotRequestEventRow[] };
          const rawItems = Array.isArray(payload?.items) ? payload.items : [];
          const itemsForSupply = rawItems.filter(item => {
            const supplyDraftId = Number(item.payload_json?.supply_draft_id || 0);
            return supplyDraftId === 0 || supplyDraftId === supply.id;
          });
          setAuditEvents(itemsForSupply);
        } else {
          setAuditEvents([]);
        }
      } else {
        setAuditEvents([]);
      }
    } catch (auditError) {
      setError(auditError instanceof Error ? auditError.message : 'Ошибка загрузки истории поставки');
      setSelectedAuditRequestId(null);
      setSelectedAuditSupplyId(null);
      setSelectedAuditSupplyDetail(null);
      setAuditEvents([]);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const toggleRequestSort = useCallback((key: RequestSortKey) => {
    setRequestSortKey(currentKey => {
      if (currentKey === key) {
        setRequestSortDirection(currentDirection => (currentDirection === 'asc' ? 'desc' : 'asc'));
        return currentKey;
      }
      setRequestSortDirection('asc');
      return key;
    });
  }, []);

  const toggleRequestStatusFilter = useCallback((statusRaw: string) => {
    const status = String(statusRaw || '').trim().toLowerCase();
    if (!status) return;
    setActiveRequestStatusFilters(current => {
      const nextMode = cycleStatusFilterMode(current[status] || 'neutral');
      if (nextMode === 'neutral') {
        const { [status]: _, ...rest } = current;
        return rest;
      }
      return { ...current, [status]: nextMode };
    });
  }, []);

  const clearRequestStatusFilters = useCallback(() => {
    setActiveRequestStatusFilters(defaultStatusFilterState());
  }, []);

  const toggleSupplySort = useCallback((key: SupplySortKey) => {
    setSupplySortKey(currentKey => {
      if (currentKey === key) {
        setSupplySortDirection(currentDirection => (currentDirection === 'asc' ? 'desc' : 'asc'));
        return currentKey;
      }
      setSupplySortDirection('asc');
      return key;
    });
  }, []);

  const toggleSupplyStatusFilter = useCallback((statusRaw: string) => {
    const status = String(statusRaw || '').trim().toLowerCase();
    if (!status) return;
    setActiveSupplyStatusFilters(current => {
      const nextMode = cycleStatusFilterMode(current[status] || 'neutral');
      if (nextMode === 'neutral') {
        const { [status]: _, ...rest } = current;
        return rest;
      }
      return { ...current, [status]: nextMode };
    });
  }, []);

  const clearSupplyStatusFilters = useCallback(() => {
    setActiveSupplyStatusFilters(defaultStatusFilterState());
  }, []);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  useEffect(() => {
    if (transportCacheInitialized) return;
    let cachedProfile: Partial<TransportHistoryProfile> | null = null;
    if (typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem(transportCacheStorageKey);
        if (raw) {
          cachedProfile = JSON.parse(raw) as Partial<TransportHistoryProfile>;
        }
      } catch {
        cachedProfile = null;
      }
    }
    const fallbackProfile = latestAssignedTransportProfile || topTransportProfiles[0];
    if (!cachedProfile && !fallbackProfile) {
      return;
    }
    const profile = cachedProfile || fallbackProfile;
    if (profile) {
      setTransportEditorDriverName(String(profile.driverName || '').trim());
      setTransportEditorDriverPhone(String(profile.driverPhone || '').trim());
      setTransportEditorVehicleBrand(String(profile.vehicleBrand || '').trim());
      setTransportEditorVehiclePlate(String(profile.vehiclePlate || '').trim());
    }
    setTransportCacheInitialized(true);
  }, [latestAssignedTransportProfile, topTransportProfiles, transportCacheInitialized]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.WebSocket !== 'function') return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    let disposed = false;
    let reconnectTimeoutId: number | null = null;
    let socket: WebSocket | null = null;
    const connect = () => {
      if (disposed) return;
      try {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const liveUrl = `${protocol}//${window.location.host}/api/ai/ozon-supplies/live?token=${encodeURIComponent(token)}`;
        socket = new window.WebSocket(liveUrl);
      } catch (socketError) {
        console.error(socketError);
        setLiveSocketConnected(false);
        reconnectTimeoutId = window.setTimeout(connect, 3000);
        return;
      }
      socket.onopen = () => {
        setLiveSocketConnected(true);
      };
      socket.onmessage = event => {
        try {
          const payload = JSON.parse(String(event.data || ''));
          if (payload?.type === 'snapshot') {
            applyLiveSnapshot(payload as OzonSuppliesLiveSnapshotMessage);
          }
        } catch (socketError) {
          console.error(socketError);
        }
      };
      socket.onerror = () => {
        setLiveSocketConnected(false);
      };
      socket.onclose = () => {
        setLiveSocketConnected(false);
        if (!disposed) {
          reconnectTimeoutId = window.setTimeout(connect, 3000);
        }
      };
    };
    connect();
    return () => {
      disposed = true;
      setLiveSocketConnected(false);
      if (reconnectTimeoutId !== null) {
        window.clearTimeout(reconnectTimeoutId);
      }
      if (socket && (socket.readyState === window.WebSocket.OPEN || socket.readyState === window.WebSocket.CONNECTING)) {
        socket.close();
      }
    };
  }, [applyLiveSnapshot]);

  useEffect(() => {
    setBulkRecommendationWarehouse(commonRecommendationWarehouse);
  }, [commonRecommendationWarehouse, recommendationTaskMode, recommendationsModalOpen]);

  useEffect(() => {
    if (!recommendationWarehouseNotice) return undefined;
    const timeoutId = window.setTimeout(() => setRecommendationWarehouseNotice(''), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [recommendationWarehouseNotice]);

  useEffect(() => {
    setRecommendationWindowStartDisplay(formatCompactDateInput(recommendationWindowStart));
  }, [recommendationWindowStart]);

  useEffect(() => {
    setRecommendationWindowEndDisplay(formatCompactDateInput(recommendationWindowEnd));
  }, [recommendationWindowEnd]);

  useEffect(() => {
    if (liveSocketConnected) return undefined;
    const hasActiveCargoJobs = supplies.some(row => {
      const status = String(row.latest_cargo_job?.status || '').trim().toLowerCase();
      return status === 'queued' || status === 'running';
    });
    const hasLiveRequests = requests.some(row => liveRequestStatuses.has(String(row.status || '').trim().toLowerCase()));
    const hasLiveSupplies = supplies.some(row => liveSupplyStatuses.has(getEffectiveSupplyStatus(row)));
    if (!hasActiveCargoJobs && !hasLiveRequests && !hasLiveSupplies) return undefined;
    const intervalId = window.setInterval(() => {
      load().catch(console.error);
    }, 5000);
    return () => window.clearInterval(intervalId);
  }, [liveSocketConnected, load, requests, supplies]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.WebSocket !== 'function') return undefined;
    const jobId = String(clusterSupplyRecomputeJob?.job_id || '').trim();
    if (!jobId || !clusterSupplyRecomputeActive) return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    let disposed = false;
    let reconnectTimeoutId: number | null = null;
    let socket: WebSocket | null = null;
    const connect = () => {
      if (disposed) return;
      try {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const liveUrl = `${protocol}//${window.location.host}/api/ai/jobs/live?token=${encodeURIComponent(token)}&jobIds=${encodeURIComponent(jobId)}`;
        socket = new window.WebSocket(liveUrl);
      } catch (socketError) {
        console.error(socketError);
        reconnectTimeoutId = window.setTimeout(connect, 3000);
        return;
      }
      socket.onmessage = event => {
        try {
          const payload = JSON.parse(String(event.data || '')) as { type?: string; items?: BackgroundJobResponse[] };
          if (payload?.type !== 'jobs_snapshot' || !Array.isArray(payload.items)) return;
          const nextJob = payload.items.find(item => String(item?.job_id || '').trim() === jobId);
          if (!nextJob) return;
          setClusterSupplyRecomputeJob(nextJob);
          const status = String(nextJob.status || nextJob.state || '').trim().toLowerCase();
          if (status === 'completed' || status === 'failed') {
            load().catch(console.error);
          }
        } catch (socketError) {
          console.error(socketError);
        }
      };
      socket.onclose = () => {
        if (!disposed) {
          reconnectTimeoutId = window.setTimeout(connect, 3000);
        }
      };
    };
    connect();
    return () => {
      disposed = true;
      if (reconnectTimeoutId !== null) {
        window.clearTimeout(reconnectTimeoutId);
      }
      if (socket && (socket.readyState === window.WebSocket.OPEN || socket.readyState === window.WebSocket.CONNECTING)) {
        socket.close();
      }
    };
  }, [clusterSupplyRecomputeActive, clusterSupplyRecomputeJob?.job_id, load]);

  useEffect(() => {
    const jobId = String(clusterSupplyRecomputeJob?.job_id || '').trim();
    if (!jobId) return undefined;
    const currentStatus = String(clusterSupplyRecomputeJob?.status || clusterSupplyRecomputeJob?.state || '').trim().toLowerCase();
    if (currentStatus === 'completed' || currentStatus === 'failed') {
      return undefined;
    }
    let cancelled = false;
    const pollStatus = async () => {
      try {
        const response = await apiFetch(`/api/ai/cluster-supply/recommendations/run-status?jobId=${encodeURIComponent(jobId)}`);
        if (!response.ok) return;
        const nextJob = (await response.json()) as BackgroundJobResponse;
        if (cancelled) return;
        const nextStatus = String(nextJob?.status || nextJob?.state || '').trim().toLowerCase();
        setClusterSupplyRecomputeJob(nextJob);
        if ((nextStatus === 'completed' || nextStatus === 'failed') && nextStatus !== currentStatus) {
          load().catch(console.error);
        }
      } catch (pollError) {
        console.error(pollError);
      }
    };
    pollStatus().catch(console.error);
    const intervalId = window.setInterval(() => {
      pollStatus().catch(console.error);
    }, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [clusterSupplyRecomputeJob?.job_id, clusterSupplyRecomputeJob?.state, clusterSupplyRecomputeJob?.status, load]);

  const readinessIndicators = useMemo(() => {
    const checks = runtimeReadiness?.checks;
    const resolveClassName = (ready: boolean | null | undefined) => {
      if (loading || ready === undefined || ready === null) return 'text-slate-300';
      return ready ? 'text-emerald-500' : 'text-rose-500';
    };
    return [
      { key: 'slot_tables', title: 'Tenant таблицы', className: resolveClassName(checks?.slot_tables?.ready), Icon: DatabaseStatusIcon },
      { key: 'credentials', title: 'Ключи OZON', className: resolveClassName(checks?.credentials?.ready), Icon: KeyStatusIcon },
      { key: 'ai_runtime', title: 'AI runtime', className: resolveClassName(checks?.ai_runtime?.ready), Icon: AIRuntimeStatusIcon },
      { key: 'manual_mode', title: 'Ручной режим', className: resolveClassName(checks?.manual_mode?.ready ?? true), Icon: ManualModeStatusIcon },
    ];
  }, [loading, runtimeReadiness]);

  const summaryCards = [
    ['Заявки всего', summary?.requests_total],
    ['Поставки всего', summary?.supplies_total],
    ['В поиске слота', summary?.searching_requests],
    ['Забронировано', summary?.slot_booked_supplies],
    ['Заполнение грузомест', summary?.cargo_packing_required_supplies],
    ['Ожидают отгрузки', summary?.awaiting_shipment_supplies],
    ['В отслеживании', summary?.in_supply_tracking_supplies],
    ['Завершено', summary?.completed_supplies],
  ] as const;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mx-auto flex max-w-7xl flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Поставки OZON</h1>
            <p className="mt-1 text-sm text-slate-500">
              Черновики заявок на слот, draft-поставки и фактический lifecycle поставок OZON.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {readinessIndicators.map(item => (
              <span
                key={item.key}
                title={item.title}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white"
              >
                <item.Icon className={`h-4 w-4 ${item.className}`} />
              </span>
            ))}
            <button
              type="button"
              onClick={() => load().catch(console.error)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
            >
              Обновить
            </button>
          </div>
        </div>

        <VelocityAnomalyBanner />
        <PortfolioReslotBanner />

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}
        {formNotice && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {formNotice}
          </div>
        )}

        <section className={cardClass}>
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Рекомендации</h2>
              <p className="mt-1 text-sm text-slate-500">
                Сводка по актуальным рекомендациям, которые еще можно завести в новые заявки OZON.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => { triggerManualClusterSupplyRecompute().catch(console.error); }}
                disabled={manualRecomputeBusy || clusterSupplyRecomputeActive}
                className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                title="Запустить пересчёт рекомендаций вручную. Автоматически выполняется раз в сутки."
              >
                {manualRecomputeBusy || clusterSupplyRecomputeActive ? 'Пересчёт идёт...' : 'Пересчитать рекомендации'}
              </button>
              <button
                type="button"
                onClick={() => openRecommendationsPlanner().catch(console.error)}
                disabled={recommendationPlanningBusy || (!clusterSupplyRecomputeActive && activeRecommendations.length === 0)}
                className="rounded-2xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {recommendationPlanningBusy ? 'Актуализация...' : 'Запланировать'}
              </button>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-sm text-slate-500">Кластеры к планированию</div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">{formatNumber(recommendationSummary.clusterCount, 0)}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-sm text-slate-500">SKU к планированию</div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">{formatNumber(recommendationSummary.skuCount, 0)}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-sm text-slate-500">Товаров к планированию, шт</div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">{formatNumber(recommendationSummary.totalQuantity, 0)}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-sm text-slate-500">Коробок к планированию</div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">{formatNumber(recommendationSummary.totalBoxes, 0)}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-sm text-slate-500">Размеры коробок к планированию</div>
              <div className="mt-2 text-sm font-medium text-slate-900">{recommendationSummary.boxLabel}</div>
            </div>
          </div>
          {clusterSupplyRecomputeActive && (
            <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
              Рекомендации пересчитываются. В модалке планирования будет показан loader до завершения пересчета.
            </div>
          )}
          {!clusterSupplyRecomputeActive && activeRecommendations.length === 0 && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              Активных рекомендаций для планирования сейчас нет.
            </div>
          )}
        </section>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {summaryCards.map(([label, value]) => (
            <div key={String(label)} className="rounded-3xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
              <div className="text-sm text-slate-500">{label}</div>
              <div className="mt-2 text-3xl font-semibold text-slate-900">{summary === null ? '—' : (value ?? 0)}</div>
            </div>
          ))}
        </div>

        <section className={cardClass}>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Заявки на слот</h2>
              <p className="mt-1 text-sm text-slate-500">
                Очередь поиска слотов: одна заявка задает окно поиска и потом разлагается в конкретные поставки по кластерам.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                {filteredRequests.length} записей
              </span>
              <button
                type="button"
                onClick={openNewRequestEditor}
                className="rounded-2xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
              >
                Новая заявка
              </button>
            </div>
          </div>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={requestSearch}
              onChange={event => setRequestSearch(event.target.value)}
              placeholder="Поиск: заявка, статус, склад, товар, SKU, кластер..."
              className="min-w-[320px] flex-1 rounded-2xl border border-slate-200 px-4 py-2 text-sm outline-none focus:border-blue-400"
            />
            <button
              type="button"
              onClick={clearRequestStatusFilters}
              className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Всего: {requests.length}
            </button>
            {requestStatusChips.map(chip => (
              <button
                key={chip.status}
                type="button"
                onClick={() => toggleRequestStatusFilter(chip.status)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                  chip.mode === 'include'
                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : chip.mode === 'exclude'
                      ? 'border-rose-200 bg-rose-50 text-rose-700'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                {chip.label}: {chip.count}
              </button>
            ))}
          </div>
          <div className="max-h-[58vh] overflow-auto rounded-2xl border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgb(226,232,240)]">
                <tr className="text-left text-slate-500">
                  <th className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => toggleRequestSort('title')} className="inline-flex items-center gap-1 font-medium hover:text-slate-900">
                      Заявка
                      <SortIndicator active={requestSortKey === 'title'} direction={requestSortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium min-w-[180px]">
                    <button type="button" onClick={() => toggleRequestSort('status')} className="inline-flex items-center gap-1 font-medium hover:text-slate-900">
                      Статус
                      <SortIndicator active={requestSortKey === 'status'} direction={requestSortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => toggleRequestSort('slot')} className="inline-flex items-center gap-1 font-medium hover:text-slate-900">
                      Слот / OZON
                      <SortIndicator active={requestSortKey === 'slot'} direction={requestSortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => toggleRequestSort('source_warehouse')} className="inline-flex items-center gap-1 font-medium hover:text-slate-900">
                      Склад
                      <SortIndicator active={requestSortKey === 'source_warehouse'} direction={requestSortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => toggleRequestSort('items')} className="inline-flex items-center gap-1 font-medium hover:text-slate-900">
                      Позиции
                      <SortIndicator active={requestSortKey === 'items'} direction={requestSortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedRequests.map(row => (
                  <tr key={row.id} className="align-top">
                    <td className="px-3 py-3">
                      <div className="font-medium text-slate-900">{`Заявка #${row.id}`}</div>
                      <div className="mt-1 text-xs text-slate-500">
                        {Array.from(new Set(row.items.map(item => String(item.cluster_name || '').trim()).filter(Boolean))).join(', ') || '—'}
                      </div>
                      <div className="mt-2">
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                          {requestTypeLabelMap[row.request_type] || row.request_type || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 min-w-[180px]">
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                        {statusLabelMap[row.status] || row.status || '—'}
                      </span>
                      {(() => {
                        const retryWait = formatRetryWait(row.earliest_retry_at);
                        if (!retryWait) return null;
                        const retryCount = Number(row.retry_count || 0);
                        return (
                          <div className="mt-1 flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                            <span>⏸</span>
                            <span>Повтор через {retryWait}</span>
                            {retryCount > 0 && <span className="text-amber-500">· {retryCount}x</span>}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      {(() => {
                        const supplySummary = requestSupplySummaryByRequestId.get(row.id);
                        if (!supplySummary) {
                          return '—';
                        }
                        return (
                          <div className="space-y-1 text-xs">
                            <div className={supplySummary.slotLabel.source === 'confirmed' ? 'font-medium text-slate-900' : 'text-slate-700'}>
                              {supplySummary.slotLabel.primary}
                            </div>
                            {supplySummary.slotLabel.secondary && supplySummary.ozonSupplyId && (
                              <div className="text-slate-500">{supplySummary.slotLabel.secondary}</div>
                            )}
                            {!supplySummary.ozonSupplyId && supplySummary.ozonOrderId ? (
                              <div className="text-slate-500">Заявка OZON #{supplySummary.ozonOrderId}</div>
                            ) : null}
                            {supplySummary.ozonSupplyId ? (
                              <div className="text-slate-500">Поставка OZON #{supplySummary.ozonSupplyId}</div>
                            ) : null}
                            {supplySummary.quantityAdjustment?.missing_total && supplySummary.quantityAdjustment.missing_total > 0 ? (
                              <div>
                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                                  Коррекция OZON: −{formatNumber(supplySummary.quantityAdjustment.missing_total, 0)} шт
                                </span>
                              </div>
                            ) : null}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      <div>{row.source_warehouse || '—'}</div>
                      <div className="mt-2">
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                          Грузоместа: {row.auto_create_cargo_places === false ? 'Ручное' : 'Авто'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1 text-slate-700">
                        {row.items.slice(0, 3).map(item => (
                          <div key={item.id} className="text-xs">
                            {recommendationArticleBySku(item.sku) || item.sku} · {item.quantity} шт
                          </div>
                        ))}
                        {row.items.length > 3 && (
                          <div className="text-xs text-slate-500">+{row.items.length - 3} поз.</div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-2">
                        <IconButton title="Редактировать" onClick={() => populateFormFromRequest(row)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton title="Информация" onClick={() => openAudit(row.id).catch(console.error)}>
                          <InfoIcon />
                        </IconButton>
                        {row.status === 'draft' && (
                          <IconButton title="Запустить поиск" onClick={() => updateRequestStatus(row.id, 'start_search').catch(console.error)} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                            <PlayIcon />
                          </IconButton>
                        )}
                        {row.status === 'searching' && (
                          <IconButton title="Остановить поиск" onClick={() => updateRequestStatus(row.id, 'stop_search').catch(console.error)} className="border-amber-200 text-amber-700 hover:bg-amber-50">
                            <PauseIcon />
                          </IconButton>
                        )}
                        {!['completed', 'cancelled', 'failed'].includes(row.status) && (
                          <IconButton title="Отменить заявку" onClick={() => updateRequestStatus(row.id, 'cancel').catch(console.error)} className="border-rose-200 text-rose-700 hover:bg-rose-50">
                            <CancelIcon />
                          </IconButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && sortedRequests.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-500">
                      Пока нет заявок на слот.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_rgb(226,232,240)]">
                <tr>
                  <td colSpan={6} className="px-3 py-2 text-xs font-medium text-slate-500">
                    Всего заявок: {sortedRequests.length}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        <section className={cardClass}>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Поставки</h2>
              <p className="mt-1 text-sm text-slate-500">
                Конкретные draft-поставки OZON: слот, грузоместа, ТС и дальнейшее движение до завершения.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <IconButton
                title="Заполнить грузоместа по дате"
                onClick={() => openSupplyDateAction('cargo_fill')}
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              >
                <CargoIcon />
              </IconButton>
              <IconButton
                title="Скачать PDF-реестр по дате"
                onClick={() => openSupplyDateAction('registry')}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              >
                <RegistryIcon />
              </IconButton>
              <IconButton
                title={transportCacheReady ? 'Кеш ТС и водителя заполнен' : 'Настроить кеш ТС и водителя'}
                onClick={openTransportCacheEditor}
                className={
                  transportCacheReady
                    ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                    : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                }
              >
                <VehicleIcon />
              </IconButton>
              <IconButton
                title="Расходы на доставку"
                onClick={() => setDeliveryCostOpen(true)}
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                  <path d="M10.75 10.818a5.977 5.977 0 0 0 3.498-1.32 6 6 0 0 1-1.672 3.338l-.36.362a3 3 0 0 1-4.243 0l-.36-.36a6.002 6.002 0 0 1-1.67-3.34 5.977 5.977 0 0 0 3.496 1.32h1.31Z" />
                  <path d="M10 2a.75.75 0 0 1 .75.75v.258a33.186 33.186 0 0 1 6.668.83.75.75 0 0 1-.336 1.461 31.28 31.28 0 0 0-1.103-.232l1.702 7.545a.75.75 0 0 1-.387.832A4.981 4.981 0 0 1 15 14c-.825 0-1.606-.2-2.294-.556a.75.75 0 0 1-.387-.832l1.77-7.849a31.743 31.743 0 0 0-3.339-.364v11.851h1.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1 0-1.5h1.5V4.399a31.712 31.712 0 0 0-3.339.364l1.77 7.849a.75.75 0 0 1-.387.832A4.981 4.981 0 0 1 5 14c-.825 0-1.606-.2-2.294-.556a.75.75 0 0 1-.387-.832l1.702-7.545c-.372.07-.74.148-1.103.232a.75.75 0 0 1-.336-1.462 33.053 33.053 0 0 1 6.668-.829V2.75A.75.75 0 0 1 10 2ZM5 12.662l-1.395-6.177C4.6 7.643 5.8 8.5 5 8.5s.4-.857 1.395-2.015L5 12.662ZM15 12.662l-1.395-6.177c.995 1.158 1.395 2.015 1.395 2.015s.4-.857 1.395-2.015L15 12.662Z" />
                </svg>
              </IconButton>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                {filteredSupplies.length} записей
              </span>
            </div>
          </div>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={openSuppliesDateFilter}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              <CalendarIcon />
              {suppliesDateFilterMode === 'single' ? 'Дата слота' : 'Диапазон слотов'}: {suppliesDateFilterLabel}
            </button>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              Показаны поставки с датой слота в фильтре
            </span>
          </div>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={supplySearch}
              onChange={event => setSupplySearch(event.target.value)}
              placeholder="Поиск: поставка, статус, кластер, склад, товар, SKU, ТС, водитель..."
              className="min-w-[320px] flex-1 rounded-2xl border border-slate-200 px-4 py-2 text-sm outline-none focus:border-blue-400"
            />
            <button
              type="button"
              onClick={() => { setSupplyActiveOnly(false); clearSupplyStatusFilters(); }}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                !supplyActiveOnly
                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                  : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
              }`}
            >
              Всего: {suppliesByDateFilter.length}
            </button>
            <button
              type="button"
              onClick={() => { setSupplyActiveOnly(true); clearSupplyStatusFilters(); }}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                supplyActiveOnly
                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                  : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
              }`}
            >
              Активные: {suppliesByDateFilter.filter(r => !TERMINAL_SUPPLY_STATUSES.has(getEffectiveSupplyStatus(r))).length}
            </button>
            {supplyStatusChips.map(chip => (
              <button
                key={chip.status}
                type="button"
                onClick={() => toggleSupplyStatusFilter(chip.status)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                  chip.mode === 'include'
                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : chip.mode === 'exclude'
                      ? 'border-rose-200 bg-rose-50 text-rose-700'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                {chip.label}: {chip.count}
              </button>
            ))}
          </div>
          <div className="max-h-[58vh] overflow-auto rounded-2xl border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgb(226,232,240)]">
                <tr className="text-left text-slate-500">
                  <th className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => toggleSupplySort('id')} className="inline-flex items-center gap-1 font-medium hover:text-slate-900">
                      Поставка
                      <SortIndicator active={supplySortKey === 'id'} direction={supplySortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium min-w-[180px]">
                    <button type="button" onClick={() => toggleSupplySort('status')} className="inline-flex items-center gap-1 font-medium hover:text-slate-900">
                      Статус
                      <SortIndicator active={supplySortKey === 'status'} direction={supplySortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => toggleSupplySort('slot')} className="inline-flex items-center gap-1 font-medium hover:text-slate-900">
                      Слот
                      <SortIndicator active={supplySortKey === 'slot'} direction={supplySortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => toggleSupplySort('transport')} className="inline-flex items-center gap-1 font-medium hover:text-slate-900">
                      ТС / водитель
                      <SortIndicator active={supplySortKey === 'transport'} direction={supplySortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => toggleSupplySort('items')} className="inline-flex items-center gap-1 font-medium hover:text-slate-900">
                      Позиции
                      <SortIndicator active={supplySortKey === 'items'} direction={supplySortDirection} />
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedSupplies.map(row => {
                  const transportAssigned = hasTransportAssignmentData(row);
                  const cargoPlacesReady = hasCargoPlacesData(row);
                  return (
                  <tr key={row.id} className="align-top">
                    <td className="px-3 py-3">
                      {(() => {
                  const quantityAdjustment = getSupplyQuantityAdjustment(row);
                  const cargoMismatch = getSupplyCargoQuantityMismatch(row);
                  const displayedCargoPlacesCount = getDisplayCargoPlacesCount(row);
                  const overdue = isSupplyOverdue(row, defaultSupplyToday);
                  const missingCargoPlaces = hasMissingCargoPlaces(row);
                  const cargoJobError = missingCargoPlaces && hasCargoJobError(row);
                  const ozonOrderLink = row.ozon_order_id
                    ? `https://seller.ozon.ru/app/supply/orders/${Number(row.ozon_order_id)}`
                    : '';
                        return (
                          <>
                            {ozonOrderLink ? (
                              <a
                                href={ozonOrderLink}
                                target="_blank"
                                rel="noreferrer"
                                className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                                title={`Открыть заявку OZON #${Number(row.ozon_order_id)}`}
                              >
                                Поставка #{row.id}
                              </a>
                            ) : (
                              <div className="font-medium text-slate-900">Поставка #{row.id}</div>
                            )}
                            <div className="mt-1 text-xs text-slate-500">{row.target_cluster || '—'}</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                                Склад: {row.source_warehouse || '—'}
                              </span>
                              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                                Грузоместа: {displayedCargoPlacesCount}
                              </span>
                              {overdue ? (
                                <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-medium text-rose-700">
                                  Просрочено
                                </span>
                              ) : null}
                              {missingCargoPlaces ? (
                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700">
                                  Нет грузомест
                                </span>
                              ) : null}
                              {cargoJobError ? (
                                <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-medium text-rose-700">
                                  Ошибка грузомест
                                </span>
                              ) : null}
                              {quantityAdjustment?.missing_total && quantityAdjustment.missing_total > 0 ? (
                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700">
                                  Коррекция OZON: −{formatNumber(quantityAdjustment.missing_total, 0)} шт
                                </span>
                              ) : null}
                              {quantityAdjustment?.cargo_repack_required ? (
                                <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-medium text-rose-700">
                                  Нужна перепаковка
                                </span>
                              ) : null}
                              {cargoMismatch ? (
                                <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-medium text-rose-700">
                                  Расхождение: OZON {formatNumber(cargoMismatch.approved, 0)} / грузоместа {formatNumber(cargoMismatch.packed, 0)}
                                </span>
                              ) : null}
                            </div>
                          </>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3 min-w-[180px]">
                      <div className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 inline-flex">
                        {getEffectiveSupplyStatusLabel(row)}
                      </div>
                      {getSupplyRawOzonState(row) && (
                        <div className="mt-1 text-xs text-slate-500">
                          OZON: {getSupplyRawOzonState(row)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      {(() => {
                        const slotLabel = getSupplySlotLabel(row);
                        return (
                          <div className="space-y-1 text-xs">
                            <div className={slotLabel.source === 'confirmed' ? 'font-medium text-slate-900' : 'text-slate-700'}>
                              {slotLabel.primary}
                            </div>
                            {slotLabel.secondary && row.ozon_supply_id && (
                              <div className="text-slate-500">{slotLabel.secondary}</div>
                            )}
                            {!row.ozon_supply_id && row.ozon_order_id ? (
                              <div className="text-slate-500">Заявка OZON #{row.ozon_order_id}</div>
                            ) : null}
                            {row.ozon_supply_id ? (
                              <div className="text-slate-500">Поставка OZON #{row.ozon_supply_id}</div>
                            ) : null}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      <div className="text-xs">
                        {row.transport_assignment?.vehicle
                          ? `${row.transport_assignment.vehicle.brand || 'ТС'} · ${row.transport_assignment.vehicle.license_plate || '—'}`
                          : '—'}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {row.transport_assignment?.driver
                          ? `${row.transport_assignment.driver.full_name || '—'} · ${row.transport_assignment.driver.phone || '—'}`
                          : 'Водитель не назначен'}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1 text-slate-700">
                        {row.items.slice(0, 3).map(item => (
                          <div key={item.id} className="text-xs">
                            {recommendationArticleBySku(item.sku) || item.sku} · {item.quantity} шт
                          </div>
                        ))}
                        {row.items.length > 3 && (
                          <div className="text-xs text-slate-500">+{row.items.length - 3} поз.</div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-2">
                        <IconButton title="Информация" onClick={() => openSupplyInfo(row).catch(console.error)}>
                          <InfoIcon />
                        </IconButton>
                        <IconButton
                          title={transportAssigned ? 'ТС и водитель заполнены' : 'Назначить ТС и водителя'}
                          onClick={() => assignCachedTransportForSupply(row).catch(console.error)}
                          disabled={
                            formBusy ||
                            supplyCancellingIds.includes(row.id) ||
                            transportAssigningSupplyIds.includes(row.id) ||
                            isSupplyExecutionLocked(row)
                          }
                          className={
                            transportAssigned
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-100'
                              : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                          }
                        >
                          <VehicleIcon className={transportAssigningSupplyIds.includes(row.id) ? 'animate-spin' : ''} />
                        </IconButton>
                        <IconButton
                          title={cargoPlacesReady ? 'Грузоместа заполнены' : 'Создать грузоместа'}
                          onClick={() => fillSupplyCargoPlaces(row.id).catch(console.error)}
                          disabled={
                            formBusy ||
                            supplyCancellingIds.includes(row.id) ||
                            isSupplyExecutionLocked(row) ||
                            ['queued', 'running'].includes(String(row.latest_cargo_job?.status || '').trim().toLowerCase())
                          }
                          className={
                            cargoPlacesReady
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-100'
                              : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                          }
                        >
                          <CargoIcon />
                        </IconButton>
                        <IconButton
                          title={isSupplyCancelable(row) ? 'Отменить поставку' : 'Отмена поставки недоступна'}
                          onClick={() => cancelSupply(row).catch(console.error)}
                          disabled={
                            formBusy ||
                            supplyCancellingIds.includes(row.id) ||
                            !isSupplyCancelable(row)
                          }
                          className="border-rose-200 text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <CancelIcon className={supplyCancellingIds.includes(row.id) ? 'animate-spin' : ''} />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                )})}
                {!loading && sortedSupplies.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-500">
                      По выбранному фильтру нет поставок.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_rgb(226,232,240)]">
                <tr>
                  <td colSpan={6} className="px-3 py-2 text-xs font-medium text-slate-500">
                    Всего поставок: {sortedSupplies.length}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        <section className={cardClass}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-slate-900">Диагностика slot-worker</h2>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
              jobs: {workerDiagnostics?.total_jobs ?? 0}
            </span>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-7">
            <div className="rounded-2xl border border-slate-200 p-3 text-sm">
              <div className="font-medium text-slate-900">Средн. скан заявок</div>
              <div className="mt-1 text-slate-700">{workerDiagnostics?.averages?.scanned_requests ?? 0}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-3 text-sm">
              <div className="font-medium text-slate-900">Средн. найдено слотов</div>
              <div className="mt-1 text-slate-700">{workerDiagnostics?.averages?.slot_found ?? 0}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-3 text-sm">
              <div className="font-medium text-slate-900">Средн. бронирований</div>
              <div className="mt-1 text-slate-700">{workerDiagnostics?.averages?.slot_booked ?? 0}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-3 text-sm">
              <div className="font-medium text-slate-900">Средн. pending</div>
              <div className="mt-1 text-slate-700">{workerDiagnostics?.averages?.candidates_pending ?? 0}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-3 text-sm">
              <div className="font-medium text-slate-900">Booking pending</div>
              <div className="mt-1 text-slate-700">{workerDiagnostics?.averages?.slot_booking_pending ?? 0}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-3 text-sm">
              <div className="font-medium text-slate-900">Booking failed</div>
              <div className="mt-1 text-slate-700">{workerDiagnostics?.averages?.slot_booking_failed ?? 0}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-3 text-sm">
              <div className="font-medium text-slate-900">Slot API calls</div>
              <div className="mt-1 text-slate-700">{workerDiagnostics?.averages?.slot_api_calls ?? 0}</div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
            <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-700">
              retry_scheduled: {workerDiagnostics?.retry_scheduled ?? 0}
            </span>
            <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-rose-700">
              dead_letter: {workerDiagnostics?.dead_letter ?? 0}
            </span>
            {Object.entries(workerDiagnostics?.status_counts || {}).map(([statusKey, count]) => (
              <span key={statusKey} className="rounded-full border border-slate-200 px-2.5 py-1">
                {statusKey}: {count}
              </span>
            ))}
          </div>
        </section>

        {recommendationsModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
            <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Планирование заявок по рекомендациям</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setRecommendationsModalOpen(false)}
                  className="rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50"
                >
                  ×
                </button>
              </div>
              <div className="flex flex-col overflow-y-auto overflow-x-hidden px-5 pb-4">
                <div className="sticky top-0 z-20 -mx-5 border-b border-slate-200 bg-white px-5 pb-2 pt-3">
                  {clusterSupplyRecomputeActive ? (
                    <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 h-5 w-5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
                        <div>
                          <div className="text-sm font-semibold text-blue-900">Рекомендации пересчитываются</div>
                          <div className="mt-1 text-sm text-blue-700">
                            {clusterSupplyRecomputeMessage || 'Ждем завершения фонового пересчета, чтобы показать актуальный список к планированию.'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="inline-flex rounded-2xl bg-slate-100 p-0.5">
                          <button
                            type="button"
                            onClick={() => setRecommendationTaskMode('cluster')}
                            className={`rounded-2xl px-3 py-1.5 text-sm font-medium transition ${recommendationTaskMode === 'cluster' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
                          >
                            Кластер
                          </button>
                          <button
                            type="button"
                            onClick={() => setRecommendationTaskMode('sku')}
                            className={`rounded-2xl px-3 py-1.5 text-sm font-medium transition ${recommendationTaskMode === 'sku' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
                          >
                            SKU
                          </button>
                        </div>
                        <div className="min-w-[220px] flex-1 sm:max-w-xs">
                          <select
                            value={bulkRecommendationWarehouse}
                            onChange={event => {
                              setBulkRecommendationWarehouse(event.target.value);
                              applyBulkRecommendationWarehouse(event.target.value);
                            }}
                            className="w-full rounded-2xl border border-slate-200 px-3 py-1.5 text-sm text-slate-700 outline-none focus:border-blue-400"
                          >
                            <option value="">Склад отправления</option>
                            {recommendationWarehouseOptionsMerged.map(warehouse => (
                              <option key={warehouse} value={warehouse}>{warehouse}</option>
                            ))}
                          </select>
                        </div>
                        <div className="text-xs text-slate-500">
                          Активно задач: {selectedRecommendationTasks.length} из {recommendationTasksSelection.length}
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        {recommendationWarehouseNotice && (
                          <div className="text-xs font-medium text-emerald-700">{recommendationWarehouseNotice}</div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-3 space-y-3">
                  {clusterSupplyRecomputeActive ? (
                    <div className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-8 text-center">
                      <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
                      <div className="mt-4 text-sm font-semibold text-blue-900">Подготавливаем актуальные рекомендации</div>
                      <div className="mt-2 text-sm text-blue-700">
                        {clusterSupplyRecomputeMessage || 'После завершения пересчета здесь появятся задачи для планирования заявок.'}
                      </div>
                    </div>
                  ) : recommendationTasksSelection.map(task => {
                    const expanded = expandedRecommendationTaskIds.includes(task.id);
                    const disabled = disabledRecommendationTaskIds.includes(task.id);
                    const priority = priorityMeta(task.selectedPriorityScore || task.priorityScore);
                    return (
                      <div key={task.id} className={`rounded-2xl border p-4 transition ${disabled ? 'border-slate-200 bg-slate-50 opacity-70' : 'border-slate-200 bg-white'}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <label className="flex min-w-0 flex-1 items-start gap-3">
                            <input
                              type="checkbox"
                              checked={!disabled}
                              onChange={() => toggleRecommendationTaskDisabled(task.id)}
                              className="mt-1"
                            />
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="truncate text-sm font-semibold text-slate-900">{task.title}</div>
                                <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${priority.className}`}>
                                  {priority.label}
                                </span>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                                  {recommendationTaskMode === 'cluster' ? `SKU: ${formatNumber(task.selectedSkuCount, 0)}` : `Кластеры: ${formatNumber(task.selectedClusterCount, 0)}`}
                                </span>
                                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                                  Всего: {formatNumber(task.selectedQuantity, 0)}
                                </span>
                                {renderBoxChips(task.selectedBoxBySize)}
                                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                                  Коробок: {formatNumber(task.selectedBoxes, 0)}
                                </span>
                              </div>
                            </div>
                          </label>
                          <div className="flex flex-wrap items-end gap-2">
                            <label className="flex min-w-[220px] flex-col gap-1 text-xs text-slate-500">
                              <span>Склад отправления</span>
                              <select
                                value={task.effectiveSourceWarehouse}
                                onChange={event => updateRecommendationTaskWarehouse(task.id, event.target.value)}
                                className="rounded-2xl border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-blue-400"
                              >
                                <option value="">Не выбран</option>
                                {recommendationWarehouseOptionsMerged.map(warehouse => (
                                  <option key={warehouse} value={warehouse}>{warehouse}</option>
                                ))}
                              </select>
                            </label>
                            <button
                              type="button"
                              onClick={() => toggleRecommendationTaskExpanded(task.id)}
                              className="inline-flex h-10 w-10 items-center justify-center self-end rounded-2xl border border-slate-200 text-slate-700 hover:border-slate-300 hover:text-slate-900"
                              title={expanded ? 'Свернуть' : 'Развернуть'}
                            >
                              <svg viewBox="0 0 20 20" fill="none" className={`h-4 w-4 transition ${expanded ? 'rotate-180' : ''}`} aria-hidden="true">
                                <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        {expanded && (
                          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                            <div className="grid grid-cols-[minmax(0,2fr)_100px_220px_40px_56px] gap-3 bg-slate-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                              <div>{recommendationTaskMode === 'cluster' ? 'Артикул товара' : 'Кластер'}</div>
                              <div className="text-center">Шт</div>
                              <div>Коробки</div>
                              <div className="text-center">?</div>
                              <div className="text-center">Вкл</div>
                            </div>
                            <div className="divide-y divide-slate-100">
                              {task.details.map(detail => {
                                const detailExcluded = excludedRecommendationIds.includes(detail.recommendationId);
                                const detailPriority = priorityMeta(detail.priorityScore);
                                const reasonExpanded = expandedRecommendationReasonIds.includes(detail.recommendationId);
                                const reasonCard = buildRecommendationReasonCard(detail, recommendationTaskMode);
                                const effectiveQty = recommendationQtyOverrides.get(detail.recommendationId) ?? detail.quantity;
                                const effectiveBoxPlan = recommendationBoxOverrides.get(detail.recommendationId) ?? null;
                                const effectiveBoxBySize: Map<string, number> = effectiveBoxPlan
                                  ? new Map(effectiveBoxPlan.map(b => [b.box_size, b.boxes_used]))
                                  : detail.boxBySize;
                                const effectiveTotalBoxes = effectiveBoxPlan
                                  ? effectiveBoxPlan.reduce((s, b) => s + b.boxes_used, 0)
                                  : detail.totalBoxes;
                                const isQtyOverridden = recommendationQtyOverrides.has(detail.recommendationId);
                                return (
                                  <div key={`${task.id}-${detail.recommendationId}`} className={`${detailExcluded ? 'bg-slate-50/80 opacity-60' : ''}`}>
                                    <div className="grid grid-cols-[minmax(0,2fr)_100px_220px_40px_56px] gap-3 px-4 py-3 text-sm text-slate-700">
                                      <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                        {recommendationTaskMode === 'cluster' ? (
                                          <>
                                              <div className="truncate font-medium text-slate-900">{detail.article}</div>
                                              <span className="truncate text-xs text-slate-500">SKU {detail.sku}</span>
                                          </>
                                        ) : (
                                            <div className="truncate font-medium text-slate-900">{detail.clusterName}</div>
                                          )}
                                          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${detailPriority.className}`}>
                                            {detailPriority.label}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex items-center justify-center font-medium text-slate-900">
                                        {formatNumber(effectiveQty, 0)}
                                        {isQtyOverridden && <span className="ml-1 text-[10px] text-blue-500">ред.</span>}
                                      </div>
                                      <div className="flex flex-wrap items-center gap-2">
                                        {renderBoxChips(effectiveBoxBySize)}
                                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
                                          Коробок: {formatNumber(effectiveTotalBoxes, 0)}
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => toggleRecommendationReasonExpanded(detail.recommendationId)}
                                        className="flex h-8 w-8 items-center justify-center self-center rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:border-slate-300 hover:text-slate-900"
                                        title="Показать обоснование"
                                      >
                                        ?
                                      </button>
                                      <label className="flex items-center justify-center">
                                        <input
                                          type="checkbox"
                                          checked={!detailExcluded}
                                          onChange={() => toggleRecommendationDetailExcluded(detail.recommendationId)}
                                        />
                                      </label>
                                    </div>
                                    {reasonExpanded && (
                                      <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-4">
                                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                                          <div className="flex flex-wrap items-start justify-between gap-2">
                                            <div className="text-sm font-semibold text-slate-900">{reasonCard.title}</div>
                                            {reasonCard.badges.length > 0 ? (
                                              <div className="flex flex-wrap justify-end gap-2">
                                                {reasonCard.badges.map((badge, badgeIndex) => (
                                                  <span
                                                    key={`${detail.recommendationId}-badge-${badgeIndex}`}
                                                    className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700"
                                                  >
                                                    {badge}
                                                  </span>
                                                ))}
                                              </div>
                                            ) : null}
                                          </div>
                                          <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-3">
                                            {reasonCard.metrics.map(metric => {
                                              const valueClass = metric.tone === 'warning'
                                                ? 'text-amber-700'
                                                : metric.tone === 'accent'
                                                  ? 'text-sky-700'
                                                  : 'text-slate-900';
                                              return (
                                                <div key={`${detail.recommendationId}-${metric.label}`} className="rounded-md bg-slate-50 px-3 py-2">
                                                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{metric.label}</div>
                                                  <div className={`mt-1 text-sm font-semibold ${valueClass}`}>{metric.value}</div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                          {reasonCard.note ? (
                                            <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
                                              {reasonCard.note}
                                            </div>
                                          ) : null}
                                          {reasonCard.footnote ? (
                                            <div className="mt-2 text-xs text-slate-500">{reasonCard.footnote}</div>
                                          ) : null}
                                          {/* --- Inline editing: quantity + box plan --- */}
                                          <div className="mt-4 border-t border-slate-100 pt-4">
                                            <div className="flex items-center gap-3">
                                              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Количество</span>
                                              <input
                                                type="number"
                                                min={1}
                                                value={effectiveQty}
                                                onChange={e => handleQtyOverride(detail.recommendationId, e.target.valueAsNumber, detail)}
                                                className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-center text-sm font-medium text-slate-900 outline-none focus:border-blue-400"
                                              />
                                              {isQtyOverridden && (
                                                <span className="text-xs text-slate-400">рекомендовано: {detail.quantity}</span>
                                              )}
                                            </div>
                                            <div className="mt-3">
                                              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Коробки</div>
                                              <div className="flex flex-wrap gap-2">
                                                {(() => {
                                                  const plan: ClusterSupplyRecommendationBoxPlan[] = effectiveBoxPlan
                                                    || (detail.metaJson?.boxes_plan_json as ClusterSupplyRecommendationBoxPlan[] | undefined)
                                                    || [];
                                                  const allSizes = ['S', 'M', 'L', 'XL', 'CXL'];
                                                  const planMap = new Map(plan.map(b => [b.box_size, b]));
                                                  return allSizes.map(size => {
                                                    const box = planMap.get(size);
                                                    const count = box?.boxes_used ?? 0;
                                                    const unitsPerBox = box?.units_per_box ?? 0;
                                                    const isActive = count > 0;
                                                    return (
                                                      <div key={size} className={`flex items-center gap-1 rounded-xl border px-2.5 py-1.5 transition ${isActive ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}>
                                                        <button
                                                          type="button"
                                                          onClick={() => handleBoxChange(detail.recommendationId, size, -1, detail)}
                                                          disabled={count === 0}
                                                          className="flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-200 disabled:opacity-30"
                                                        >&minus;</button>
                                                        <span className="min-w-[44px] text-center text-xs font-semibold text-slate-800">{size} &times;{count}</span>
                                                        <button
                                                          type="button"
                                                          onClick={() => handleBoxChange(detail.recommendationId, size, 1, detail)}
                                                          className="flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 text-xs font-medium text-slate-600 hover:bg-blue-100"
                                                        >+</button>
                                                        {count > 0 && unitsPerBox > 0 && (
                                                          <span className="ml-1 text-[10px] text-slate-400">({count * unitsPerBox} шт)</span>
                                                        )}
                                                      </div>
                                                    );
                                                  });
                                                })()}
                                              </div>
                                              {(() => {
                                                const plan: ClusterSupplyRecommendationBoxPlan[] = effectiveBoxPlan
                                                  || (detail.metaJson?.boxes_plan_json as ClusterSupplyRecommendationBoxPlan[] | undefined)
                                                  || [];
                                                const total = plan.reduce((s, b) => s + b.planned_units, 0);
                                                const diff = total - detail.quantity;
                                                return total > 0 ? (
                                                  <div className="mt-2 text-xs text-slate-500">
                                                    Итого: {total} шт
                                                    {diff !== 0 && (
                                                      <span className={diff > 0 ? 'ml-1 text-amber-600' : 'ml-1 text-blue-600'}>
                                                        ({diff > 0 ? '+' : ''}{diff} к рекомендации)
                                                      </span>
                                                    )}
                                                  </div>
                                                ) : null;
                                              })()}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!clusterSupplyRecomputeActive && recommendationTasksSelection.length === 0 && (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600">
                      Для выбранного режима группировки рекомендаций пока нет задач.
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t border-slate-200 px-5 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <span className="font-medium">С</span>
                      <div className="relative w-[148px]">
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="ДД.ММ.ГГ"
                          value={recommendationWindowStartDisplay}
                          onChange={event => setRecommendationWindowStartDisplay(event.target.value)}
                          onBlur={() => {
                            const normalized = parseCompactDateInput(recommendationWindowStartDisplay);
                            if (normalized) {
                              setRecommendationWindowStart(normalized);
                              setRecommendationWindowStartDisplay(formatCompactDateInput(normalized));
                              return;
                            }
                            setRecommendationWindowStartDisplay(formatCompactDateInput(recommendationWindowStart));
                          }}
                          onKeyDown={event => {
                            if (event.key !== 'Enter') return;
                            const normalized = parseCompactDateInput(recommendationWindowStartDisplay);
                            if (normalized) {
                              setRecommendationWindowStart(normalized);
                              setRecommendationWindowStartDisplay(formatCompactDateInput(normalized));
                              return;
                            }
                            setRecommendationWindowStartDisplay(formatCompactDateInput(recommendationWindowStart));
                          }}
                          className="w-full rounded-2xl border border-slate-200 px-3 py-1.5 pr-9 text-sm outline-none focus:border-blue-400"
                        />
                        <input
                          type="date"
                          value={recommendationWindowStart}
                          onChange={event => {
                            const val = event.target.value;
                            if (val) {
                              setRecommendationWindowStart(val);
                              setRecommendationWindowStartDisplay(formatCompactDateInput(val));
                            }
                          }}
                          className="absolute inset-0 w-full cursor-pointer opacity-0"
                          tabIndex={-1}
                        />
                        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                          <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
                            <path d="M6 2.75V5M14 2.75V5M3.75 7.25h12.5M5.5 4h9A1.75 1.75 0 0 1 16.25 5.75v8.75A1.75 1.75 0 0 1 14.5 16.25h-9a1.75 1.75 0 0 1-1.75-1.75V5.75A1.75 1.75 0 0 1 5.5 4Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      </div>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <span className="font-medium">По</span>
                      <div className="relative w-[148px]">
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="ДД.ММ.ГГ"
                          value={recommendationWindowEndDisplay}
                          onChange={event => setRecommendationWindowEndDisplay(event.target.value)}
                          onBlur={() => {
                            const normalized = parseCompactDateInput(recommendationWindowEndDisplay);
                            if (normalized) {
                              setRecommendationWindowEnd(normalized);
                              setRecommendationWindowEndDisplay(formatCompactDateInput(normalized));
                              return;
                            }
                            setRecommendationWindowEndDisplay(formatCompactDateInput(recommendationWindowEnd));
                          }}
                          onKeyDown={event => {
                            if (event.key !== 'Enter') return;
                            const normalized = parseCompactDateInput(recommendationWindowEndDisplay);
                            if (normalized) {
                              setRecommendationWindowEnd(normalized);
                              setRecommendationWindowEndDisplay(formatCompactDateInput(normalized));
                              return;
                            }
                            setRecommendationWindowEndDisplay(formatCompactDateInput(recommendationWindowEnd));
                          }}
                          className="w-full rounded-2xl border border-slate-200 px-3 py-1.5 pr-9 text-sm outline-none focus:border-blue-400"
                        />
                        <input
                          type="date"
                          value={recommendationWindowEnd}
                          onChange={event => {
                            const val = event.target.value;
                            if (val) {
                              setRecommendationWindowEnd(val);
                              setRecommendationWindowEndDisplay(formatCompactDateInput(val));
                            }
                          }}
                          className="absolute inset-0 w-full cursor-pointer opacity-0"
                          tabIndex={-1}
                        />
                        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                          <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
                            <path d="M6 2.75V5M14 2.75V5M3.75 7.25h12.5M5.5 4h9A1.75 1.75 0 0 1 16.25 5.75v8.75A1.75 1.75 0 0 1 14.5 16.25h-9a1.75 1.75 0 0 1-1.75-1.75V5.75A1.75 1.75 0 0 1 5.5 4Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      </div>
                    </label>
                  </div>
                  <label
                    className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700"
                    title="Продолжать искать слоты и при более удачном варианте перебронировать дату, чтобы по возможности собрать поставки на один день"
                  >
                    <input
                      type="checkbox"
                      checked={recommendationTrackingEnabled}
                      onChange={event => setRecommendationTrackingEnabled(event.target.checked)}
                    />
                    <span>Включить компановку в один день поставки</span>
                  </label>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    {renderBoxChips(selectedRecommendationBoxSummary)}
                  </div>
                  <button
                    type="button"
                    onClick={() => createRecommendationRequests().catch(console.error)}
                    disabled={clusterSupplyRecomputeActive || recommendationSubmitBusy || selectedRecommendationTasks.length === 0}
                    className="rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {recommendationSubmitBusy ? 'Создание...' : 'Создать заявки'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {transportCacheEditorOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
            <div className="w-full max-w-2xl rounded-3xl border border-slate-200 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Кеш ТС и водителя</h3>
                  <p className="text-sm text-slate-500">Эта связка будет подставляться при нажатии на иконку ТС в строке поставки.</p>
                </div>
                <button
                  type="button"
                  onClick={closeTransportEditor}
                  className="rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50"
                >
                  ×
                </button>
              </div>
              <div className="space-y-4 px-6 py-5">
                {topTransportProfiles.length > 0 && (
                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Популярные связки из прошедших поставок</div>
                    <div className="flex flex-wrap gap-2">
                      {topTransportProfiles.map(profile => (
                        <button
                          key={profile.profileKey}
                          type="button"
                          onClick={() => applyTransportHistoryProfile(profile)}
                          className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:-translate-y-px hover:border-slate-300"
                        >
                          {profile.vehiclePlate || 'Без номера'}{profile.driverName ? ` · ${profile.driverName}` : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="flex flex-col gap-2 text-sm text-slate-700">
                    <span className="font-medium">ФИО водителя</span>
                    <input
                      value={transportEditorDriverName}
                      onChange={event => {
                        const value = event.target.value;
                        setTransportEditorDriverName(value);
                        const normalized = value.trim().toLowerCase();
                        const match = transportHistoryProfiles.find(item => item.driverName.trim().toLowerCase() === normalized);
                        if (match && !transportEditorDriverPhone.trim()) setTransportEditorDriverPhone(match.driverPhone);
                        if (match && !transportEditorVehicleBrand.trim()) setTransportEditorVehicleBrand(match.vehicleBrand);
                        if (match && !transportEditorVehiclePlate.trim()) setTransportEditorVehiclePlate(match.vehiclePlate);
                      }}
                      list="transport-driver-name-options"
                      className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                      placeholder="Иванов Иван Иванович"
                    />
                  </label>
                  <label className="flex flex-col gap-2 text-sm text-slate-700">
                    <span className="font-medium">Телефон</span>
                    <input
                      value={transportEditorDriverPhone}
                      onChange={event => {
                        const value = event.target.value;
                        setTransportEditorDriverPhone(value);
                        const normalized = value.trim();
                        const match = transportHistoryProfiles.find(item => item.driverPhone.trim() === normalized);
                        if (match && !transportEditorDriverName.trim()) setTransportEditorDriverName(match.driverName);
                        if (match && !transportEditorVehicleBrand.trim()) setTransportEditorVehicleBrand(match.vehicleBrand);
                        if (match && !transportEditorVehiclePlate.trim()) setTransportEditorVehiclePlate(match.vehiclePlate);
                      }}
                      list="transport-driver-phone-options"
                      className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                      placeholder="+7 999 000-00-00"
                    />
                  </label>
                  <label className="flex flex-col gap-2 text-sm text-slate-700">
                    <span className="font-medium">Номер ТС</span>
                    <input
                      value={transportEditorVehiclePlate}
                      onChange={event => {
                        const value = event.target.value;
                        setTransportEditorVehiclePlate(value);
                        const normalized = value.trim().toLowerCase();
                        const match = transportHistoryProfiles.find(item => item.vehiclePlate.trim().toLowerCase() === normalized);
                        if (match && !transportEditorVehicleBrand.trim()) setTransportEditorVehicleBrand(match.vehicleBrand);
                        if (match && !transportEditorDriverName.trim()) setTransportEditorDriverName(match.driverName);
                        if (match && !transportEditorDriverPhone.trim()) setTransportEditorDriverPhone(match.driverPhone);
                      }}
                      list="transport-vehicle-plate-options"
                      className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                      placeholder="А123АА77"
                    />
                  </label>
                  <label className="flex flex-col gap-2 text-sm text-slate-700">
                    <span className="font-medium">Марка / модель ТС</span>
                    <input
                      value={transportEditorVehicleBrand}
                      onChange={event => setTransportEditorVehicleBrand(event.target.value)}
                      list="transport-vehicle-brand-options"
                      className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                      placeholder="Газель Next"
                    />
                  </label>
                </div>
                <datalist id="transport-driver-name-options">
                  {transportDriverNameOptions.map(value => <option key={`dn-${value}`} value={value} />)}
                </datalist>
                <datalist id="transport-driver-phone-options">
                  {transportDriverPhoneOptions.map(value => <option key={`dp-${value}`} value={value} />)}
                </datalist>
                <datalist id="transport-vehicle-plate-options">
                  {transportVehiclePlateOptions.map(value => <option key={`vp-${value}`} value={value} />)}
                </datalist>
                <datalist id="transport-vehicle-brand-options">
                  {transportVehicleBrandOptions.map(value => <option key={`vb-${value}`} value={value} />)}
                </datalist>
                <div className="text-xs text-slate-500">
                  Новые водитель и ТС сохранятся в справочник автоматически при назначении.
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
                <button
                  type="button"
                  onClick={() => assignTransportByFilteredDate().catch(console.error)}
                  disabled={formBusy || !resolvedBulkTransportDate || resolvedBulkTransportEditableCount <= 0}
                  title={
                    !resolvedBulkTransportDate
                      ? 'Для массового назначения выберите одну дату в фильтре блока "Поставки"'
                      : resolvedBulkTransportEditableCount > 0
                        ? 'Назначить всем поставкам без водителя на выбранную дату слота'
                        : 'На выбранную дату нет поставок, где ещё можно назначать ТС и водителя'
                  }
                  className="rounded-2xl border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Назначить всем {resolvedBulkTransportDate ? formatDate(resolvedBulkTransportDate) : '—'}
                </button>
                <button
                  type="button"
                  onClick={closeTransportEditor}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={saveTransportCache}
                  disabled={formBusy || !transportCacheReady}
                  className="rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Сохранить кеш
                </button>
              </div>
            </div>
          </div>
        )}

        {supplyDateActionMode && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
            <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {supplyDateActionMode === 'cargo_fill' ? 'Заполнение грузомест' : 'PDF-реестр поставок'}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {supplyDateActionMode === 'cargo_fill'
                      ? 'Укажите дату слота, для которой нужно поставить заполнение грузомест в работу.'
                      : 'Укажите дату слота, для которой нужно сформировать и скачать PDF-реестр.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeSupplyDateAction}
                  className="rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={formBusy}
                >
                  ×
                </button>
              </div>
              <div className="space-y-4 px-6 py-5">
                {supplyDateActionMode === 'registry' && (
                  <div className="inline-flex rounded-2xl bg-slate-100 p-1">
                    <button
                      type="button"
                      onClick={() => setSupplyDateActionDraftMode('single')}
                      className={`rounded-2xl px-3 py-1.5 text-sm font-medium transition ${supplyDateActionDraftMode === 'single' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
                    >
                      Одна дата
                    </button>
                    <button
                      type="button"
                      onClick={() => setSupplyDateActionDraftMode('range')}
                      className={`rounded-2xl px-3 py-1.5 text-sm font-medium transition ${supplyDateActionDraftMode === 'range' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
                    >
                      Диапазон
                    </button>
                  </div>
                )}
                <div className={`grid gap-4 ${(supplyDateActionMode === 'registry' && supplyDateActionDraftMode === 'range') ? 'sm:grid-cols-2' : 'sm:grid-cols-1'}`}>
                  <label className="flex flex-col gap-2 text-sm text-slate-700">
                    <span className="font-medium">
                      {supplyDateActionMode === 'registry' && supplyDateActionDraftMode === 'range' ? 'Дата с' : 'Дата слота'}
                    </span>
                    <input
                      type="date"
                      value={supplyDateActionDraft}
                      onChange={event => setSupplyDateActionDraft(event.target.value)}
                      className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                    />
                  </label>
                  {supplyDateActionMode === 'registry' && supplyDateActionDraftMode === 'range' && (
                    <label className="flex flex-col gap-2 text-sm text-slate-700">
                      <span className="font-medium">Дата по</span>
                      <input
                        type="date"
                        value={supplyDateActionDraftEnd}
                        onChange={event => setSupplyDateActionDraftEnd(event.target.value)}
                        className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                      />
                    </label>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
                <button
                  type="button"
                  onClick={closeSupplyDateAction}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={formBusy}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={() => submitSupplyDateAction().catch(console.error)}
                  className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={formBusy}
                >
                  {supplyDateActionMode === 'cargo_fill' ? 'Заполнить' : 'Скачать'}
                </button>
              </div>
            </div>
          </div>
        )}

        {suppliesDateFilterOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
            <div className="w-full max-w-xl rounded-3xl border border-slate-200 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Фильтр поставок по дате слота</h3>
                  <p className="text-sm text-slate-500">По умолчанию дата сдвигается к самой ранней поставке со статусом от «Ожидает отгрузки». Отменённые поставки игнорируются.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSuppliesDateFilterOpen(false)}
                  className="rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50"
                >
                  ×
                </button>
              </div>
              <div className="space-y-4 px-6 py-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="inline-flex rounded-2xl bg-slate-100 p-1">
                    <button
                      type="button"
                      onClick={() => setSuppliesDateFilterDraftMode('single')}
                      className={`rounded-2xl px-3 py-1.5 text-sm font-medium transition ${suppliesDateFilterDraftMode === 'single' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
                    >
                      Одна дата
                    </button>
                    <button
                      type="button"
                      onClick={() => setSuppliesDateFilterDraftMode('range')}
                      className={`rounded-2xl px-3 py-1.5 text-sm font-medium transition ${suppliesDateFilterDraftMode === 'range' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
                    >
                      Диапазон
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={setSuppliesDateFilterToToday}
                      className="rounded-2xl border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      Сегодня
                    </button>
                    <button
                      type="button"
                      onClick={resetSuppliesDateFilterToWeek}
                      className="rounded-2xl border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      Ближайшие 7 дней
                    </button>
                  </div>
                </div>
                <div className={`grid gap-4 ${suppliesDateFilterDraftMode === 'range' ? 'sm:grid-cols-2' : 'sm:grid-cols-1'}`}>
                  <label className="flex flex-col gap-2 text-sm text-slate-700">
                    <span className="font-medium">{suppliesDateFilterDraftMode === 'single' ? 'Дата' : 'Дата с'}</span>
                    <input
                      type="date"
                      value={suppliesDateFilterDraftStart}
                      onChange={event => setSuppliesDateFilterDraftStart(event.target.value)}
                      className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                    />
                  </label>
                  {suppliesDateFilterDraftMode === 'range' && (
                    <label className="flex flex-col gap-2 text-sm text-slate-700">
                      <span className="font-medium">Дата по</span>
                      <input
                        type="date"
                        value={suppliesDateFilterDraftEnd}
                        onChange={event => setSuppliesDateFilterDraftEnd(event.target.value)}
                        className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                      />
                    </label>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setSuppliesDateFilterOpen(false)}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={applySuppliesDateFilter}
                  className="rounded-2xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
                >
                  Применить
                </button>
              </div>
            </div>
          </div>
        )}

        {requestEditorOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
            <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {editingRequestId ? `Редактирование заявки #${editingRequestId}` : 'Новая заявка на слот'}
                  </h3>
                  <p className="text-sm text-slate-500">Ручное создание и редактирование заявки без inline-контролов на странице.</p>
                </div>
                <button
                  type="button"
                  onClick={closeRequestEditor}
                  className="rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50"
                >
                  ×
                </button>
              </div>
              <div className="max-h-[72vh] overflow-auto px-6 py-5">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <label className="flex flex-col gap-2 text-sm text-slate-700">
                    <span className="font-medium">Тип заявки</span>
                    <select
                      value={requestType}
                      onChange={event => setRequestType(event.target.value === 'by_cluster' ? 'by_cluster' : 'by_product')}
                      className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                    >
                      <option value="by_product">По товару</option>
                      <option value="by_cluster">По кластеру</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-2 text-sm text-slate-700">
                    <span className="font-medium">Название</span>
                    <input
                      value={title}
                      onChange={event => setTitle(event.target.value)}
                      className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                      placeholder="Например, Поставка мыльниц на ЮФО"
                    />
                  </label>
                  <label className="flex flex-col gap-2 text-sm text-slate-700">
                    <span className="font-medium">Склад отправления</span>
                    <input
                      value={sourceWarehouse}
                      onChange={event => setSourceWarehouse(event.target.value)}
                      className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                      placeholder="МО_ЗАМОСКВОРЕЧЬЕ_XD"
                    />
                  </label>
                  <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={allowPartialBooking}
                      onChange={event => setAllowPartialBooking(event.target.checked)}
                    />
                    <span>Бронировать частично, если нашли слот не на все кластеры</span>
                  </label>
                  <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={autoCreateCargoPlaces}
                      onChange={event => setAutoCreateCargoPlaces(event.target.checked)}
                    />
                    <span>Автоматически создавать грузоместа после бронирования</span>
                  </label>
                  <label className="flex flex-col gap-2 text-sm text-slate-700">
                    <span className="font-medium">Дата начала окна</span>
                    <input
                      type="date"
                      value={windowStart}
                      onChange={event => setWindowStart(event.target.value)}
                      className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                    />
                  </label>
                  <label className="flex flex-col gap-2 text-sm text-slate-700">
                    <span className="font-medium">Дата конца окна</span>
                    <input
                      type="date"
                      value={windowEnd}
                      onChange={event => setWindowEnd(event.target.value)}
                      className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                    />
                  </label>
                  <label className="md:col-span-2 xl:col-span-2 flex flex-col gap-2 text-sm text-slate-700">
                    <span className="font-medium">Комментарий</span>
                    <input
                      value={comment}
                      onChange={event => setComment(event.target.value)}
                      className="rounded-2xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-400"
                      placeholder="Внутренний комментарий менеджера"
                    />
                  </label>
                </div>

                <div className="mt-5 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Состав заявки</h3>
                    <button
                      type="button"
                      onClick={() => setItems(current => [...current, emptyItem()])}
                      className="rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-slate-300 hover:text-slate-900"
                    >
                      Добавить строку
                    </button>
                  </div>
                  <div className="space-y-3">
                    {items.map((item, index) => (
                      <div key={index} className="grid gap-3 rounded-2xl border border-slate-200 p-4 md:grid-cols-4 xl:grid-cols-12">
                        <input
                          value={item.cluster_name}
                          onChange={event =>
                            setItems(current =>
                              current.map((row, rowIndex) => (rowIndex === index ? { ...row, cluster_name: event.target.value } : row)),
                            )
                          }
                          className="rounded-2xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400 md:col-span-2 xl:col-span-3"
                          placeholder="Кластер"
                        />
                        <input
                          value={item.sku}
                          onChange={event =>
                            setItems(current =>
                              current.map((row, rowIndex) => (rowIndex === index ? { ...row, sku: event.target.value } : row)),
                            )
                          }
                          className="rounded-2xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400 xl:col-span-2"
                          placeholder="SKU / артикул"
                        />
                        <input
                          value={item.product_name}
                          onChange={event =>
                            setItems(current =>
                              current.map((row, rowIndex) => (rowIndex === index ? { ...row, product_name: event.target.value } : row)),
                            )
                          }
                          className="rounded-2xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400 md:col-span-2 xl:col-span-4"
                          placeholder="Название товара"
                        />
                        <input
                          value={item.quantity}
                          onChange={event =>
                            setItems(current =>
                              current.map((row, rowIndex) => (rowIndex === index ? { ...row, quantity: event.target.value } : row)),
                            )
                          }
                          className="rounded-2xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400 xl:col-span-2"
                          placeholder="Кол-во"
                        />
                        <button
                          type="button"
                          onClick={() => setItems(current => (current.length > 1 ? current.filter((_, rowIndex) => rowIndex !== index) : [emptyItem()]))}
                          className="rounded-2xl border border-rose-200 px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 xl:col-span-1"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-6 py-4">
                <button
                  type="button"
                  onClick={closeRequestEditor}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:text-slate-900"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={() => saveRequest().catch(console.error)}
                  disabled={formBusy}
                  className="rounded-2xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {editingRequestId ? 'Сохранить заявку' : 'Создать черновик'}
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedAuditRequestId !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
            <div className="max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{auditModalTitle}</h3>
                  <p className="text-sm text-slate-500">
                    История поиска, результатов запросов и служебных операций.
                    {selectedAuditSupplyId ? ` Поставка #${selectedAuditSupplyId} · заявка #${selectedAuditRequestId}.` : ''}
                  </p>
                </div>
                <button
                  type="button"
                    onClick={() => {
                      setSelectedAuditRequestId(null);
                      setSelectedAuditSupplyId(null);
                      setSelectedAuditSupplyDetail(null);
                      setAuditModalTitle('Информация');
                      setAuditEvents([]);
                    }}
                  className="rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50"
                >
                  ×
                </button>
              </div>
              <div className="max-h-[65vh] overflow-auto p-6">
                {auditLoading && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    Загрузка аудита...
                  </div>
                )}
                {!auditLoading && selectedAuditRequestRow && (
                  <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Создана</div>
                        <div className="mt-1 text-sm text-slate-800">{formatDateTime(selectedAuditRequestRow.created_at)}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Тип</div>
                        <div className="mt-1 text-sm text-slate-800">
                          {requestTypeLabelMap[selectedAuditRequestRow.request_type] || selectedAuditRequestRow.request_type || '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Последний поиск</div>
                        <div className="mt-1 text-sm text-slate-800">
                          {selectedAuditRequestRow.latest_search_event
                            ? `${summarizeLatestSearchEvent(selectedAuditRequestRow.latest_search_event)}${selectedAuditRequestRow.latest_search_event.created_at ? ` ${formatDateTime(selectedAuditRequestRow.latest_search_event.created_at)}` : ''}`
                            : 'Поиск еще не запускался'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Искать до</div>
                        <div className="mt-1 text-sm text-slate-800">
                          {selectedAuditRequestRow.slot_window_end_date ? formatDateTime(selectedAuditRequestRow.slot_window_end_date) : '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Заявка OZON</div>
                        <div className="mt-1 text-sm text-slate-800">
                          {selectedAuditRequestSupplySummary?.ozonOrderId ? `#${selectedAuditRequestSupplySummary.ozonOrderId}` : '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Поставка OZON</div>
                        <div className="mt-1 text-sm text-slate-800">
                          {selectedAuditRequestSupplySummary?.ozonSupplyId ? `#${selectedAuditRequestSupplySummary.ozonSupplyId}` : '—'}
                        </div>
                      </div>
                      {selectedAuditRequestSupplySummary?.quantityAdjustment?.missing_total && selectedAuditRequestSupplySummary.quantityAdjustment.missing_total > 0 ? (
                        <div className="md:col-span-2">
                          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Коррекция OZON</div>
                          <div className="mt-1 text-sm text-slate-800">
                            Подтверждено {formatNumber(selectedAuditRequestSupplySummary.quantityAdjustment.approved_total ?? null, 0)} из {formatNumber(selectedAuditRequestSupplySummary.quantityAdjustment.requested_total ?? null, 0)} шт.
                            Остаток {formatNumber(selectedAuditRequestSupplySummary.quantityAdjustment.missing_total, 0)} шт нужно допланировать.
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
                {!auditLoading && selectedAuditSupplyRow && (
                  <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Кластер</div>
                        <div className="mt-1 text-sm text-slate-800">{selectedAuditSupplyRow.target_cluster || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Склад</div>
                        <div className="mt-1 text-sm text-slate-800">{selectedAuditSupplyRow.source_warehouse || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Статус</div>
                        <div className="mt-1 text-sm text-slate-800">
                          {getEffectiveSupplyStatusLabel(selectedAuditSupplyRow)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Слот</div>
                        <div className="mt-1 text-sm text-slate-800">{getSupplySlotLabel(selectedAuditSupplyRow).primary}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Draft UID</div>
                        <div className="mt-1 text-sm text-slate-800">{selectedAuditSupplyRow.draft_uid || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Заявка</div>
                        <div className="mt-1 text-sm text-slate-800">
                          {selectedAuditSupplyRow.slot_request_id ? `#${selectedAuditSupplyRow.slot_request_id}` : '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Заявка OZON</div>
                        <div className="mt-1 text-sm text-slate-800">
                          {selectedAuditSupplyRow.ozon_order_id ? `#${selectedAuditSupplyRow.ozon_order_id}` : '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Поставка OZON</div>
                        <div className="mt-1 text-sm text-slate-800">
                          {selectedAuditSupplyRow.ozon_supply_id ? `#${selectedAuditSupplyRow.ozon_supply_id}` : '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Грузоместа</div>
                        <div className="mt-1 text-sm text-slate-800">{getDisplayCargoPlacesCount(selectedAuditSupplyRow)}</div>
                      </div>
                      {(() => {
                        const adjustment = getSupplyQuantityAdjustment(selectedAuditSupplyRow);
                        if (!adjustment?.missing_total || adjustment.missing_total <= 0) return null;
                        return (
                          <div>
                            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Коррекция OZON</div>
                            <div className="mt-1 text-sm text-slate-800">
                              Подтверждено {formatNumber(adjustment.approved_total ?? null, 0)} из {formatNumber(adjustment.requested_total ?? null, 0)} шт
                            </div>
                            <div className="mt-1 text-sm text-slate-600">
                              Недостает {formatNumber(adjustment.missing_total, 0)} шт{adjustment.cargo_repack_required ? ', нужна перепаковка грузомест' : ''}.
                            </div>
                          </div>
                        );
                      })()}
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">ТС / водитель</div>
                        <div className="mt-1 text-sm text-slate-800">
                          {selectedAuditSupplyRow.transport_assignment?.vehicle
                            ? `${selectedAuditSupplyRow.transport_assignment.vehicle.brand || 'ТС'} · ${selectedAuditSupplyRow.transport_assignment.vehicle.license_plate || '—'}`
                            : '—'}
                        </div>
                        <div className="mt-1 text-sm text-slate-600">
                          {selectedAuditSupplyRow.transport_assignment?.driver
                            ? `${selectedAuditSupplyRow.transport_assignment.driver.full_name || '—'} · ${selectedAuditSupplyRow.transport_assignment.driver.phone || '—'}`
                            : 'Водитель не назначен'}
                        </div>
                      </div>
                      {selectedAuditSupplyRow.latest_cargo_job?.status && (
                        <div className="md:col-span-2">
                          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Последняя cargo job</div>
                          <div className="mt-1 text-sm text-slate-800">
                            {selectedAuditSupplyRow.latest_cargo_job.status}
                            {selectedAuditSupplyRow.latest_cargo_job.stage ? ` · ${selectedAuditSupplyRow.latest_cargo_job.stage}` : ''}
                          </div>
                          {selectedAuditSupplyRow.latest_cargo_job.message && (
                            <div className="mt-1 text-sm text-slate-600">{selectedAuditSupplyRow.latest_cargo_job.message}</div>
                          )}
                          {String(selectedAuditSupplyRow.cargo_operational?.error_message || '').trim() && (
                            <div className="mt-1 text-sm text-rose-600">{String(selectedAuditSupplyRow.cargo_operational?.error_message || '').trim()}</div>
                          )}
                        </div>
                      )}
                      <div className="md:col-span-2">
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">История статусов OZON</div>
                        {Array.isArray(selectedAuditSupplyRow.status_history) && selectedAuditSupplyRow.status_history.length > 0 ? (
                          <div className="mt-3">
                            {selectedAuditSupplyRow.status_history.map((historyRow, index) => {
                              const eventTime = getSupplyStatusHistoryEventTime(historyRow);
                              const isLast = index === selectedAuditSupplyRow.status_history!.length - 1;
                              return (
                                <div
                                  key={historyRow.id || `${historyRow.supply_state || 'state'}-${eventTime || index}`}
                                  className={`relative flex gap-3 ${isLast ? '' : 'pb-4'}`}
                                >
                                  {!isLast && (
                                    <div className="absolute bottom-0 left-[7px] top-4 w-px bg-slate-200" />
                                  )}
                                  <div className="relative z-10 mt-1 h-4 w-4 shrink-0 rounded-full border-2 border-sky-500 bg-white" />
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-slate-800">
                                      {getOzonSupplyStateLabel(historyRow.supply_state)}
                                    </div>
                                    <div className="mt-0.5 text-xs text-slate-500">
                                      {formatDateTime(eventTime)}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="mt-2 text-sm text-slate-500">История статусов пока не собрана.</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {!auditLoading && auditEvents.length === 0 && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    События пока не найдены.
                  </div>
                )}
                {!auditLoading && auditEvents.length > 0 && (
                  <div className="space-y-3">
                    {auditEvents.map(eventRow => (
                      <div key={eventRow.id} className="rounded-2xl border border-slate-200 p-4">
                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5">{eventRow.event_type || 'event'}</span>
                          <span>{formatDateTime(eventRow.created_at)}</span>
                          <span>user: {eventRow.created_by_user_id ?? 'system'}</span>
                        </div>
                        <div className="mt-2 text-sm text-slate-800">
                          {eventRow.from_status || '—'} → {eventRow.to_status || '—'}
                        </div>
                        {eventRow.message && (
                          <div className="mt-1 text-sm text-slate-600">{eventRow.message}</div>
                        )}
                        {eventRow.payload_json && Object.keys(eventRow.payload_json).length > 0 && (
                          <pre className="mt-2 overflow-auto rounded-xl bg-slate-950 p-3 text-xs text-slate-100">
                            {JSON.stringify(eventRow.payload_json, null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <Suspense fallback={null}>
        <DeliveryCostModal isOpen={deliveryCostOpen} onClose={() => setDeliveryCostOpen(false)} />
      </Suspense>
    </div>
  );
};

export default OzonSuppliesPage;
